import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../../components/ui/popover';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  ArrowLeft, CheckCircle2, Clock, DollarSign, Loader2,
  History, FileText, AlertCircle, AlertTriangle,
  Banknote, X, ZoomIn, ZoomOut, Download, Pencil, Save, XCircle,
  RotateCcw, Trash2, ExternalLink, Copy, Check, Search, ShieldOff,
  Info, ArrowRightLeft, Plus, ChevronDown, ChevronUp,
  CreditCard, Zap, RefreshCw,
} from 'lucide-react';

const fmtC = (v) => v == null ? '\u2014' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = (d) => { if (!d) return '\u2014'; try { return format(new Date(d), 'MM/dd/yy'); } catch { return d; } };
const fmtDT = (d) => { if (!d) return '\u2014'; try { return format(new Date(d), 'MMM d, yyyy h:mm a'); } catch { return d; } };

const STATUS_CFG = {
  review: { label: 'In Review', color: 'bg-amber-500/15 text-amber-600 border-amber-500/20', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-500/15 text-blue-600 border-blue-500/20', icon: CheckCircle2 },
  paid: { label: 'Paid', color: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/20', icon: DollarSign },
};

const FIELD_DISPLAY = {
  current_bill_amount: 'New Charges', discount_amount: 'Credits', opening_balance: 'BBF',
  total_amount_due: 'Total Amount Due', gl_code: 'GL Code', pay_amount: 'Pay Amount',
  payment_amount: 'Total Requested',
  service_total: 'Usage Charges', late_payment_fee: 'Late Fee', adjustments: 'Other Charges',
  payment_selection: 'Payment Sel.',
};

const EDITABLE_FIELDS = new Set([
  'current_bill_amount', 'discount_amount', 'opening_balance', 'total_amount_due',
  'gl_code', 'pay_amount', 'payment_amount', 'service_total', 'late_payment_fee', 'adjustments', 'payment_selection',
]);
const BILL_LEVEL_FIELDS = new Set([
  'current_bill_amount', 'discount_amount', 'opening_balance', 'total_amount_due',
  'late_payment_fee', 'adjustments', 'payment_selection', 'payment_amount',
]);
const PAYMENT_SEL_OPTIONS = ['New Charges', 'Total Amount Due'];

export default function PaymentFileDetail({ pfId, accountId, userId, userName, userRole, onBack, onActionComplete }) {
  const [pf, setPf] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [allUtils, setAllUtils] = useState([]);
  const [billsMap, setBillsMap] = useState({});
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [utilPage, setUtilPage] = useState(1);
  const [utilTotal, setUtilTotal] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [editRowIdx, setEditRowIdx] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [saving, setSaving] = useState(false);

  const [duplicates, setDuplicates] = useState({ duplicates: [], counts: {} });

  const [auditOpen, setAuditOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [pdfMessage, setPdfMessage] = useState(null);

  const [approveOpen, setApproveOpen] = useState(false);
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  const [revertingAll, setRevertingAll] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [removeConfirm, setRemoveConfirm] = useState(null); // {idx, utilId}
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Funds Pulled state
  const [fundsPulled, setFundsPulled] = useState([]);
  const [fundsTotalPulled, setFundsTotalPulled] = useState(0);
  const [fundsRemaining, setFundsRemaining] = useState(0);
  const [fundsPulledOpen, setFundsPulledOpen] = useState(false);
  const [newFundAmount, setNewFundAmount] = useState('');
  const [newFundNotes, setNewFundNotes] = useState('');
  const [newFundTxnRef, setNewFundTxnRef] = useState('');
  const [newFundLlc, setNewFundLlc] = useState('');
  const [llcOptions, setLlcOptions] = useState([]);
  const [addingFund, setAddingFund] = useState(false);
  const [deletingFundId, setDeletingFundId] = useState(null);

  // Auto-generate fund pulls state
  const [autoGenPreview, setAutoGenPreview] = useState(null);
  const [autoGenLoading, setAutoGenLoading] = useState(false);
  const [autoGenEntries, setAutoGenEntries] = useState([]);
  const [autoGenSubmitting, setAutoGenSubmitting] = useState(false);
  const [autoGenNachaPreviewing, setAutoGenNachaPreviewing] = useState(false);

  // Line-item transaction state (writes to synced collections)
  const [txnDrawerOpen, setTxnDrawerOpen] = useState(false);
  const [txnUtil, setTxnUtil] = useState(null);
  const [txnForm, setTxnForm] = useState({ transaction_reference: '', amount: '', payment_service_charge: '', payment_method: '', notes: '', transaction_date: '' });
  const [txnSaving, setTxnSaving] = useState(false);
  const [txnMap, setTxnMap] = useState({});
  const [billTxns, setBillTxns] = useState([]);
  const [billTxnsLoading, setBillTxnsLoading] = useState(false);
  const [showAddTxnForm, setShowAddTxnForm] = useState(false);
  const [deletingTxnId, setDeletingTxnId] = useState(null);
  const [editingTxnId, setEditingTxnId] = useState(null);

  // Refund drawer state
  const [refundDrawerOpen, setRefundDrawerOpen] = useState(false);
  const [refundUtil, setRefundUtil] = useState(null);
  const [refundForm, setRefundForm] = useState({ refund_amount: '', refunded_by: '3rd Party Payment', refund_method: '' });
  const [refundSaving, setRefundSaving] = useState(false);

  // Org users for Paid By selector
  const [orgUsers, setOrgUsers] = useState([]);
  const isSuperUser = userRole === 'super_admin';

  // Paid/Unpaid filter
  const [paidFilter, setPaidFilter] = useState('all'); // all, paid, unpaid, missing_txn

  // Payment stats from shared endpoint
  const [detailPmtStats, setDetailPmtStats] = useState(null);

  // Nacha state
  const [nachaFiles, setNachaFiles] = useState([]);
  const [nachaLoading, setNachaLoading] = useState(false);
  const [nachaRegenerating, setNachaRegenerating] = useState(false);

  const [acctFilter, setAcctFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');

  // Compute distinct suppliers and account numbers for dropdown filters
  const distinctSuppliers = useMemo(() => {
    const s = new Set();
    allUtils.forEach(u => { if (u.supplier_name) s.add(u.supplier_name); });
    return [...s].sort();
  }, [allUtils]);
  const distinctAcctNos = useMemo(() => {
    const s = new Set();
    allUtils.forEach(u => { if (u.account_number) s.add(u.account_number); });
    return [...s].sort();
  }, [allUtils]);
  const [hasCreditFilter, setHasCreditFilter] = useState(false);
  const [paidByFilter, setPaidByFilter] = useState('');
  const [txnCountFilter, setTxnCountFilter] = useState('');
  const distinctPaidBy = useMemo(() => {
    const s = new Set();
    allUtils.forEach(u => { if (u.paid_by_name && u.paid_by_name !== 'None') s.add(u.paid_by_name); });
    return [...s].sort();
  }, [allUtils]);
  const [issueSummary, setIssueSummary] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);
  const [selectedIssues, setSelectedIssues] = useState(new Set());
  const [bulkDismissing, setBulkDismissing] = useState(false);
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  // Role-based access: Funds Pulled only for super_admin, admin, editor
  const canManageFunds = ['admin', 'editor'].includes(userRole) || userRole === 'super_admin';

  const sentinelRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const utilLimit = 100;

  // ── Debounce column filters ──


  // ── Data fetching ──
  const fetchPfData = useCallback(async () => {
    if (!pfId || !accountId) return;
    setLoading(true);
    try {
      const [pfRes, billsRes, auditRes] = await Promise.all([
        api.get(`/payments/v2/files/${pfId}`, { params: { account_id: accountId } }),
        api.get(`/payments/v2/files/${pfId}/bills`, { params: { account_id: accountId, page: 1, limit: 500 } }),
        api.get(`/payments/v2/files/${pfId}/audit-log`, { params: { account_id: accountId } }),
      ]);
      setPf(pfRes.data);
      const bMap = {};
      (billsRes.data.bills || []).forEach(b => { bMap[b.bill_id] = b; });
      setBillsMap(bMap);
      setAuditLog(auditRes.data.logs || []);

      // Fetch payment stats from shared endpoint
      try {
        const { data: psData } = await api.get('/payments/v2/files/batch-payment-stats', {
          params: { account_id: accountId, pf_ids: pfId },
        });
        setDetailPmtStats((psData.stats || {})[pfId] || null);
      } catch { setDetailPmtStats(null); }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load payment file');
    } finally { setLoading(false); }
  }, [pfId, accountId]);

  const fetchUtils = useCallback(async (pg = 1) => {
    if (!pfId || !accountId) return;
    if (pg > 1) setLoadingMore(true);
    try {
      const params = { account_id: accountId, page: pg, limit: utilLimit };
      if (showIssuesOnly) params.filter_issues = true;
      if (acctFilter) params.account_number = acctFilter;
      if (supplierFilter) params.supplier = supplierFilter;
      if (hasCreditFilter) params.has_credit = true;
      if (paidFilter && paidFilter !== 'all') params.payment_filter = paidFilter;
      if (paidByFilter) params.paid_by = paidByFilter;
      if (txnCountFilter) params.txn_filter = txnCountFilter;
      const { data } = await api.get(`/payments/v2/files/${pfId}/utilities`, { params });
      const utils = data.utilities || [];
      if (pg === 1) setAllUtils(utils);
      else setAllUtils(prev => [...prev, ...utils]);
      setUtilTotal(data.total || 0);
      setInvoiceCount(data.invoice_count ?? data.total ?? 0);
      // When payment_filter is active, server returns all — no more pages
      setHasMore(paidFilter && paidFilter !== 'all' ? false : utils.length >= utilLimit);
      setUtilPage(pg);
    } catch { /* silent */ } finally { setLoadingMore(false); }
  }, [pfId, accountId, showIssuesOnly, acctFilter, supplierFilter, hasCreditFilter, paidFilter, paidByFilter, txnCountFilter]);

  useEffect(() => { fetchPfData(); }, [fetchPfData]);
  useEffect(() => { fetchUtils(1); }, [fetchUtils]);

  // Load org users for Paid By selector
  useEffect(() => {
    if (accountId) {
      api.get(`/accounts/${accountId}/users-list`).then(({ data }) => {
        setOrgUsers(Array.isArray(data) ? data : []);
      }).catch(() => {});
    }
  }, [accountId]);

  // Fetch org name
  useEffect(() => {
    if (accountId) {
      api.get(`/accounts/${accountId}`).then(({ data }) => {
        setOrgName(data?.organisation_name || data?.name || '');
      }).catch(() => {});
    }
  }, [accountId]);


  const fetchChecks = useCallback(async () => {
    if (!pfId || !accountId) return;
    try {
      const [dupRes, issRes] = await Promise.all([
        api.get(`/payments/v2/files/${pfId}/duplicates`, { params: { account_id: accountId } }),
        api.get(`/payments/v2/files/${pfId}/issue-summary`, { params: { account_id: accountId } }),
      ]);
      setDuplicates(dupRes.data || { duplicates: [], counts: {} });
      setIssueSummary(issRes.data || null);
    } catch { /* silent */ }
  }, [pfId, accountId]);

  useEffect(() => { fetchChecks(); }, [fetchChecks]);

  // ── Funds Pulled ──
  const fetchFundsPulled = useCallback(async () => {
    if (!pfId || !accountId) return;
    try {
      const [fundsRes, llcRes] = await Promise.all([
        api.get(`/payments/v2/files/${pfId}/funds-pulled`, { params: { account_id: accountId } }),
        api.get(`/payments/v2/files/${pfId}/llc-options`, { params: { account_id: accountId } }),
      ]);
      setFundsPulled(fundsRes.data.entries || []);
      setFundsTotalPulled(fundsRes.data.total_pulled || 0);
      setFundsRemaining(fundsRes.data.remaining || 0);
      setLlcOptions(llcRes.data.options || []);
    } catch { /* silent */ }
  }, [pfId, accountId]);

  useEffect(() => { fetchFundsPulled(); }, [fetchFundsPulled]);

  const addFundsPulled = async () => {
    const amt = parseFloat(newFundAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!newFundTxnRef.trim()) { toast.error('Transaction reference is required'); return; }
    setAddingFund(true);
    try {
      const llcParts = newFundLlc ? newFundLlc.split('|') : ['', ''];
      await api.post(`/payments/v2/files/${pfId}/funds-pulled`, {
        account_id: accountId, amount: amt, user_id: userId || '', user_name: userName || '',
        notes: newFundNotes, transaction_reference: newFundTxnRef,
        legal_entity_name: llcParts[0] || '', ein: llcParts[1] || '',
      });
      toast.success(`Funds pulled: $${amt.toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
      setNewFundAmount(''); setNewFundNotes(''); setNewFundTxnRef(''); setNewFundLlc('');
      fetchFundsPulled();
      const auditRes = await api.get(`/payments/v2/files/${pfId}/audit-log`, { params: { account_id: accountId } });
      setAuditLog(auditRes.data.logs || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add funds'); }
    finally { setAddingFund(false); }
  };

  const deleteFundEntry = async (entryId) => {
    setDeletingFundId(entryId);
    try {
      await api.delete(`/payments/v2/files/${pfId}/funds-pulled/${entryId}`, { params: { account_id: accountId, user_id: userId || '', user_name: userName || '' } });
      toast.success('Fund entry removed');
      fetchFundsPulled();
      const auditRes = await api.get(`/payments/v2/files/${pfId}/audit-log`, { params: { account_id: accountId } });
      setAuditLog(auditRes.data.logs || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    finally { setDeletingFundId(null); }
  };

  // ── Auto-Generate Fund Pulls ──
  const loadAutoGenPreview = async () => {
    setAutoGenLoading(true);
    try {
      const { data } = await api.get(`/payments/v2/files/${pfId}/auto-generate-preview`, { params: { account_id: accountId } });
      setAutoGenPreview(data);
      // Initialize entries with auto txn refs
      setAutoGenEntries((data.entries || []).map((e, i) => ({
        ...e,
        transaction_reference: `${pf?.batch_name || 'PF'}-ACH-${String(i + 1).padStart(3, '0')}`,
        included: true,
      })));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load preview'); }
    finally { setAutoGenLoading(false); }
  };

  const submitAutoGenFunds = async () => {
    const included = autoGenEntries.filter(e => e.included);
    if (!included.length) { toast.error('No entries selected'); return; }
    setAutoGenSubmitting(true);
    try {
      const { data } = await api.post(`/payments/v2/files/${pfId}/auto-generate-funds`, {
        account_id: accountId,
        user_id: userId || '',
        user_name: userName || '',
        entries: included.map(e => ({
          legal_entity_name: e.legal_entity_name,
          ein: e.ein,
          amount: e.amount,
          transaction_reference: e.transaction_reference,
        })),
        generate_nacha: true,
      });
      toast.success(`Created ${data.entries_created} fund pulls (Total: $${data.total_pulled.toLocaleString()})`);
      setAutoGenPreview(null);
      setAutoGenEntries([]);
      fetchFundsPulled();
      fetchNachaFiles();
      const auditRes = await api.get(`/payments/v2/files/${pfId}/audit-log`, { params: { account_id: accountId } });
      setAuditLog(auditRes.data.logs || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit funds'); }
    finally { setAutoGenSubmitting(false); }
  };

  // Generate NACHA from the preview list WITHOUT submitting fund pulls or
  // persisting the NACHA file. Streams the .ach content to the browser for download.
  const previewAutoGenNacha = async () => {
    const included = autoGenEntries.filter(e => e.included);
    if (!included.length) { toast.error('No entries selected'); return; }
    setAutoGenNachaPreviewing(true);
    try {
      const { data } = await api.post(`/payments/v2/files/${pfId}/nacha/preview-generate`, {
        account_id: accountId,
        user_id: userId || '',
        user_name: userName || '',
        entries: included.map(e => ({
          legal_entity_name: e.legal_entity_name,
          ein: e.ein,
          amount: e.amount,
          transaction_reference: e.transaction_reference,
        })),
      });
      const blob = new Blob([data.content || ''], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pf?.batch_name || 'payment'}_nacha_preview.ach`;
      a.click();
      window.URL.revokeObjectURL(url);
      const skipped = data.entries_without_ach || 0;
      toast.success(
        `NACHA preview downloaded — ${data.entry_count} entries, $${(data.total_amount || 0).toLocaleString()}` +
        (skipped ? ` (${skipped} skipped — no ACH)` : '')
      );
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to preview NACHA'); }
    finally { setAutoGenNachaPreviewing(false); }
  };

  // ── Line-Item Transactions (writes to synced bill/utility collections) ──
  const fetchTransactions = useCallback(async () => {
    if (!pfId || !accountId) return;
    try {
      const { data } = await api.get(`/payments/v2/files/${pfId}/transactions`, { params: { account_id: accountId } });
      setTxnMap(data.by_utility || {});
    } catch { /* silent */ }
  }, [pfId, accountId]);

  useEffect(() => { if (pf?.status === 'approved' || pf?.status === 'paid') fetchTransactions(); }, [pf?.status, fetchTransactions]);

  const openTxnDrawer = async (util) => {
    const existing = txnMap[util.payment_file_utility_id];
    const bill = billsMap[util.bill_id] || {};
    const totalRequested = bill.payment_amount ?? util.pay_amount ?? '';
    setTxnUtil(util);
    setTxnDrawerOpen(true);
    setEditingTxnId(null);

    if ((existing && (existing.paid_amount || existing.transaction_reference)) || util.payment_status === 'paid') {
      // Existing payment or already paid (e.g. refund bills) — show LOCKED with pre-filled data
      const src = existing || util;
      setTxnForm({
        transaction_reference: src.transaction_reference || '',
        amount: src.paid_amount || totalRequested,
        payment_service_charge: src.payment_service_charge || '',
        payment_method: src.payment_method || '',
        notes: src.notes || bill.notes || '',
        transaction_date: src.transaction_date ? src.transaction_date.split('T')[0] : '',
        _totalRequested: totalRequested,
        paid_by_name: src.paid_by_name || '',
        _locked: true,
      });
      setShowAddTxnForm(false);
    } else {
      // No existing payment — show empty form
      setTxnForm({
        transaction_reference: '', amount: totalRequested,
        payment_service_charge: '', payment_method: '', notes: '',
        transaction_date: new Date().toISOString().split('T')[0],
        _totalRequested: totalRequested,
        paid_by_id: userId, paid_by_name: userName,
        _locked: false,
      });
      setShowAddTxnForm(true);
    }

    // Also fetch bill-transactions for history
    setBillTxnsLoading(true);
    try {
      if (util.bill_id) {
        const { data } = await api.get(`/payments/v2/bill-transactions/${util.bill_id}`, { params: { account_id: accountId } });
        setBillTxns(data.transactions || []);
      } else {
        setBillTxns([]);
      }
    } catch { setBillTxns([]); }
    finally { setBillTxnsLoading(false); }
  };

  const saveTxn = async () => {
    if (!txnForm.transaction_reference?.trim()) { toast.error('Transaction reference required'); return; }
    if (!txnForm.amount || parseFloat(txnForm.amount) <= 0) { toast.error('Valid amount required'); return; }
    setTxnSaving(true);
    try {
      const payload = {
        account_id: accountId,
        utility_id: txnUtil.payment_file_utility_id,
        transaction_reference: txnForm.transaction_reference,
        amount: parseFloat(txnForm.amount),
        payment_service_charge: parseFloat(txnForm.payment_service_charge || 0),
        payment_method: txnForm.payment_method || '',
        notes: txnForm.notes || '',
        paid_by: txnForm.paid_by_id || userId || '',
        paid_by_name: txnForm.paid_by_name || userName || '',
        created_by: userId || '',
        created_by_name: isSuperUser ? 'System' : (userName || ''),
        transaction_date: txnForm.transaction_date || '',
      };
      await api.post(`/payments/v2/files/${pfId}/record-payment`, payload);
      toast.success('Payment recorded');
      // Refresh bill transactions list (stay in drawer)
      if (txnUtil.bill_id) {
        const { data } = await api.get(`/payments/v2/bill-transactions/${txnUtil.bill_id}`, { params: { account_id: accountId } });
        setBillTxns(data.transactions || []);
        const bill = billsMap[txnUtil.bill_id] || {};
        const totalReq = parseFloat(bill.payment_amount ?? txnUtil.pay_amount ?? 0);
        const remaining = Math.max(0, totalReq - (data.total || 0));
        setTxnForm(prev => ({ ...prev, transaction_reference: '', amount: remaining > 0 ? remaining : '', payment_service_charge: '', notes: '', transaction_date: new Date().toISOString().split('T')[0] }));
        setShowAddTxnForm(false);
      }
      fetchTransactions();
      const utilRes = await api.get(`/payments/v2/files/${pfId}/utilities`, { params: { account_id: accountId } });
      setAllUtils(utilRes.data?.utilities || utilRes.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record payment'); }
    finally { setTxnSaving(false); }
  };

  const clearTxn = async () => {
    if (!txnUtil) return;
    setTxnSaving(true);
    try {
      await api.post(`/payments/v2/files/${pfId}/clear-payment`, {
        account_id: accountId,
        utility_id: txnUtil.payment_file_utility_id,
        transaction_reference: '',
        amount: 0,
        paid_by: userId || '',
        paid_by_name: userName || '',
      });
      toast.success('Payment cleared');
      setTxnDrawerOpen(false);
      fetchTransactions();
      const utilRes = await api.get(`/payments/v2/files/${pfId}/utilities`, { params: { account_id: accountId } });
      setAllUtils(utilRes.data?.utilities || utilRes.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clear'); }
    finally { setTxnSaving(false); }
  };

  const deleteTxnEntry = async (txnId) => {
    setDeletingTxnId(txnId);
    try {
      await api.delete(`/payments/v2/delete-transaction/${txnId}`, { params: { account_id: accountId, user_id: userId || '', user_name: userName || '' } });
      toast.success('Transaction deleted');
      // Refresh bill transactions
      if (txnUtil?.bill_id) {
        const { data } = await api.get(`/payments/v2/bill-transactions/${txnUtil.bill_id}`, { params: { account_id: accountId } });
        setBillTxns(data.transactions || []);
        const bill = billsMap[txnUtil.bill_id] || {};
        const totalReq = parseFloat(bill.payment_amount ?? txnUtil.pay_amount ?? 0);
        const remaining = Math.max(0, totalReq - (data.total || 0));
        setTxnForm(prev => ({ ...prev, amount: remaining > 0 ? remaining : '' }));
        if ((data.transactions || []).length === 0) setShowAddTxnForm(true);
      }
      fetchTransactions();
      const utilRes = await api.get(`/payments/v2/files/${pfId}/utilities`, { params: { account_id: accountId } });
      setAllUtils(utilRes.data?.utilities || utilRes.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
    finally { setDeletingTxnId(null); }
  };


  // ── Refund drawer functions ──
  const openRefundDrawer = (util) => {
    const existing = txnMap[util.payment_file_utility_id];
    const bill = billsMap[util.bill_id] || {};
    if (existing && existing.refund === 'yes') {
      setRefundForm({
        refund_amount: existing.refund_amount || bill.refund_amount || '',
        refunded_by: existing.refunded_by || bill.refunded_by || 'BillPortal',
        refund_method: existing.refund_method || bill.refund_method || '',
        refund_date: existing.refund_date || bill.refund_date || '',
        refund_reference: existing.refund_reference || bill.refund_reference || '',
        refund_to: existing.refund_to || orgName || '',
        refund_completed: !!(existing.refund_completed || bill.refund_completed),
        isEdit: true,
        _locked: true,
      });
    } else {
      setRefundForm({
        refund_amount: '',
        refunded_by: 'BillPortal',
        refund_method: '',
        refund_date: new Date().toISOString().split('T')[0],
        refund_reference: '',
        refund_to: orgName || '',
        refund_completed: false,
        isEdit: false,
        _locked: false,
      });
    }
    setRefundUtil(util);
    setRefundDrawerOpen(true);
  };

  const saveRefund = async () => {
    if (!refundForm.refund_amount || parseFloat(refundForm.refund_amount) <= 0) { toast.error('Valid refund amount required'); return; }
    setRefundSaving(true);
    try {
      await api.post(`/payments/v2/files/${pfId}/record-refund`, {
        account_id: accountId,
        utility_id: refundUtil.payment_file_utility_id,
        refund_amount: parseFloat(refundForm.refund_amount),
        refunded_by: refundForm.refunded_by || userName || '3rd Party Payment',
        refund_method: refundForm.refund_method || '',
        refund_date: refundForm.refund_date || new Date().toISOString().split('T')[0],
        refund_completed: !!refundForm.refund_completed,
        refund_reference: refundForm.refund_reference || '',
        refund_to: refundForm.refund_to || '',
        user_id: userId || '',
        user_name: userName || '',
        created_by: userId || '',
        created_by_name: isSuperUser ? 'System' : (userName || ''),
      });
      toast.success(refundForm.refund_completed ? 'Refund marked as Refunded' : 'Refund recorded (To Refund)');
      setRefundDrawerOpen(false);
      fetchTransactions();
      const utilRes = await api.get(`/payments/v2/files/${pfId}/utilities`, { params: { account_id: accountId } });
      setAllUtils(utilRes.data?.utilities || utilRes.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record refund'); }
    finally { setRefundSaving(false); }
  };

  const clearRefund = async () => {
    if (!refundUtil) return;
    setRefundSaving(true);
    try {
      await api.post(`/payments/v2/files/${pfId}/clear-refund`, {
        account_id: accountId,
        utility_id: refundUtil.payment_file_utility_id,
        refund_amount: 0,
        user_id: userId || '',
        user_name: userName || '',
      });
      toast.success('Refund cleared');
      setRefundDrawerOpen(false);
      fetchTransactions();
      const utilRes = await api.get(`/payments/v2/files/${pfId}/utilities`, { params: { account_id: accountId } });
      setAllUtils(utilRes.data?.utilities || utilRes.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clear refund'); }
    finally { setRefundSaving(false); }
  };

  // ── Download PF as CSV ──
  const downloadCsv = () => {
    if (!allUtils.length) { toast.error('No invoice data to export'); return; }
    const headers = [
      'Supplier', 'Account Number', 'Invoice Number', 'Commodity',
      'Service Start', 'Service End', 'Invoice Date', 'Due Date',
      'Usage Charges', 'BBF', 'New Charges', 'Credits', 'Late Fee', 'Other Charges',
      'Total Amt Due', (isApproved || isPaid) ? 'Approved Amt' : 'Total Requested',
      'Payment Selection', 'GL Code',
    ];
    if (isPaid || isApproved) {
      headers.push('Paid Amount', 'Txn Reference', 'Txn Date', 'Variance', 'Svc Fee',
        'Refund', 'Refund Amt', 'Refunded By', 'Refund Date', 'Refund Method');
    }
    const esc = (v) => {
      if (v == null || v === undefined) return '';
      const s = String(v);
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [headers.join(',')];
    allUtils.forEach(u => {
      const txn = txnMap[u.payment_file_utility_id] || {};
      const row = [
        esc(u.supplier_name), esc(u.account_number), esc(u.invoice_number), esc(u.commodity),
        esc(u.service_period_start), esc(u.service_period_end), esc(u.invoice_date), esc(u.due_date),
        u.service_total || '', u.opening_balance || '', u.current_bill_amount || '',
        u.discount_amount || '', u.late_payment_fee || '', u.adjustments || '',
        u.total_amount_due || '', u.payment_amount || u.pay_amount || '',
        esc(u.payment_selection), esc(u.gl_code),
      ];
      if (isPaid || isApproved) {
        const paidAmt = txn.paid_amount || u.paid_amount || '';
        const requested = parseFloat(u.payment_amount || u.pay_amount || 0);
        const paid = parseFloat(paidAmt || 0);
        row.push(paidAmt, esc(txn.transaction_reference || u.transaction_reference || ''),
          esc(txn.transaction_date || u.transaction_date || ''),
          paid > 0 ? (requested - paid).toFixed(2) : '',
          txn.payment_service_charge || u.payment_service_charge || '',
          txn.refund || u.refund || 'no',
          txn.refund_amount || u.refund_amount || '',
          esc(txn.refunded_by || u.refunded_by || ''),
          esc(txn.refund_date || u.refund_date || ''),
          esc(txn.refund_method || u.refund_method || ''));
      }
      rows.push(row.join(','));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${pf?.batch_name || pfId}_invoices.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${allUtils.length} rows`);
  };

  // ── Nacha Files ──
  const fetchNachaFiles = useCallback(async () => {
    if (!pfId || !accountId) return;
    setNachaLoading(true);
    try {
      const { data } = await api.get(`/payments/v2/files/${pfId}/nacha`, { params: { account_id: accountId } });
      setNachaFiles(data.files || []);
    } catch { /* silent */ }
    finally { setNachaLoading(false); }
  }, [pfId, accountId]);

  useEffect(() => { if (pf?.status === 'approved' || pf?.status === 'paid') fetchNachaFiles(); }, [pf?.status, fetchNachaFiles]);

  const regenerateNacha = async () => {
    setNachaRegenerating(true);
    try {
      await api.post(`/payments/v2/files/${pfId}/nacha/regenerate`, null, {
        params: { account_id: accountId, user_id: userId, user_name: userName }
      });
      toast.success('Nacha file regenerated');
      fetchNachaFiles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to regenerate'); }
    finally { setNachaRegenerating(false); }
  };

  const downloadNacha = async (nachaId) => {
    try {
      const { data } = await api.get(`/payments/v2/files/${pfId}/nacha/${nachaId}/download`, {
        params: { account_id: accountId }, responseType: 'text'
      });
      const blob = new Blob([data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pf?.batch_name || 'payment'}_nacha.ach`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    fetchUtils(utilPage + 1);
  }, [loadingMore, hasMore, utilPage, fetchUtils]);

  useEffect(() => {
    const el = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!el || !hasMore || !root) return;
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) loadMore(); }, { threshold: 0.1, root });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadMore]);

  // ── Derived lookups ──
  // Duplicate lookups - build map before reorder
  const dupInfo = useMemo(() => {
    const utilMap = {};
    for (const d of (duplicates.duplicates || [])) {
      const allIds = [];
      if (d.type === 'exact_intra_pf') {
        d.duplicate_utility_ids?.forEach(id => allIds.push(id));
      } else {
        d.utility_ids?.forEach(id => allIds.push(id));
      }
      if (d.type === 'exact_intra_pf' && d.original_utility_id) {
        if (!utilMap[d.original_utility_id]) utilMap[d.original_utility_id] = [];
        utilMap[d.original_utility_id].push({ ...d, is_original: true });
      }
      for (const id of allIds) {
        if (!utilMap[id]) utilMap[id] = [];
        utilMap[id].push(d);
      }
    }
    return utilMap;
  }, [duplicates]);

  // Reorder: original rows first, then duplicates below
  const sortedUtils = useMemo(() => {
    if (!duplicates.duplicates?.length) return allUtils;
    // Collect all type1 originals & their duplicates
    const originalSet = new Set();
    const dupSet = new Set();
    for (const d of duplicates.duplicates) {
      if (d.type === 'exact_intra_pf' && d.original_utility_id) {
        originalSet.add(d.original_utility_id);
        d.duplicate_utility_ids?.forEach(id => dupSet.add(id));
      }
    }
    if (!originalSet.size) return allUtils;

    // Build groups: original_util_id -> [original_row, ...dup_rows]
    const groups = {};
    const nonGrouped = [];
    const processed = new Set();
    for (const u of allUtils) {
      const uid = u.payment_file_utility_id;
      if (originalSet.has(uid)) {
        if (!groups[uid]) groups[uid] = { original: u, dups: [] };
        else groups[uid].original = u;
        processed.add(uid);
      } else if (dupSet.has(uid)) {
        // Find which group this belongs to
        const parentDup = (duplicates.duplicates || []).find(
          d => d.type === 'exact_intra_pf' && d.duplicate_utility_ids?.includes(uid)
        );
        if (parentDup) {
          const origId = parentDup.original_utility_id;
          if (!groups[origId]) groups[origId] = { original: null, dups: [] };
          groups[origId].dups.push(u);
          processed.add(uid);
        } else {
          nonGrouped.push(u);
        }
      } else {
        nonGrouped.push(u);
      }
    }

    // Rebuild: splice groups into position (insert where original was)
    const result = [];
    const usedGroups = new Set();
    for (const u of allUtils) {
      const uid = u.payment_file_utility_id;
      if (processed.has(uid)) {
        // Find the group for this utility
        let groupKey = null;
        if (originalSet.has(uid)) groupKey = uid;
        else {
          const parentDup = (duplicates.duplicates || []).find(
            d => d.type === 'exact_intra_pf' && d.duplicate_utility_ids?.includes(uid)
          );
          if (parentDup) groupKey = parentDup.original_utility_id;
        }
        if (groupKey && !usedGroups.has(groupKey)) {
          usedGroups.add(groupKey);
          const g = groups[groupKey];
          if (g.original) result.push(g.original);
          result.push(...g.dups);
        }
      } else {
        result.push(u);
      }
    }
    return result;
  }, [allUtils, duplicates]);

  const billFirstIdx = useMemo(() => {
    const map = {};
    sortedUtils.forEach((u, i) => { if (!(u.bill_id in map)) map[u.bill_id] = i; });
    return map;
  }, [sortedUtils]);

  // Per-utility issue flags from issue-summary API
  const issueFlagsByUtil = useMemo(() => {
    return issueSummary?.issue_flags_by_utility || {};
  }, [issueSummary]);

  // Issue set for filtering
  const issueUtilIds = useMemo(() => {
    const set = new Set();
    // From issue-summary per-utility flags
    for (const uid of Object.keys(issueFlagsByUtil)) set.add(uid);
    // From duplicate detection
    for (const u of sortedUtils) {
      if (dupInfo[u.payment_file_utility_id]) set.add(u.payment_file_utility_id);
    }
    return set;
  }, [sortedUtils, issueFlagsByUtil, dupInfo]);

  // Cross-PF duplicate bill_ids (most critical — same bill in another PF)
  const crossPfBillIds = useMemo(() => {
    const set = new Set();
    for (const d of (duplicates.duplicates || [])) {
      if (d.type === 'cross_pf_bill_id') set.add(d.bill_id);
    }
    return set;
  }, [duplicates]);

  // Display rows — server-side filter already applied, just use sortedUtils directly
  const displayUtils = sortedUtils;

  // ── Row edit ──
  // Determines which fields can be edited based on PF status + user role
  // Review (any role): payment_selection + gl_code only
  // Approved (super/admin): all editable fields
  // Approved (org admin/editor): payment_selection, current_bill_amount, payment_amount only
  const startRowEdit = (idx) => {
    const u = displayUtils[idx]; const bill = billsMap[u.bill_id] || {};
    const isPrimary = billFirstIdx[u.bill_id] === sortedUtils.indexOf(u);
    let vals = {};
    if (isReview) {
      // Review: ALL roles can only edit gl_code (utility-level) and payment_selection (bill-level, primary row)
      vals.gl_code = u.gl_code ?? '';
      if (isPrimary) vals.payment_selection = bill.payment_selection ?? '';
    } else if (isApproved && !isSuperRole) {
      // Approved + Org admin/editor: payment_selection, current_bill_amount, payment_amount only (primary row)
      if (isPrimary) {
        vals.payment_selection = bill.payment_selection ?? '';
        vals.current_bill_amount = bill.current_bill_amount ?? '';
        vals.payment_amount = bill.payment_amount ?? '';
      }
    } else {
      // Approved + Super admin OR ops role: full edit
      vals = { gl_code: u.gl_code ?? '', pay_amount: u.pay_amount ?? '', service_total: u.service_total ?? '' };
      if (isPrimary) {
        vals.current_bill_amount = bill.current_bill_amount ?? '';
        vals.discount_amount = bill.discount_amount ?? '';
        vals.opening_balance = bill.opening_balance ?? '';
        vals.total_amount_due = bill.total_amount_due ?? '';
        vals.late_payment_fee = bill.late_payment_fee ?? '';
        vals.adjustments = bill.adjustments ?? '';
        vals.payment_selection = bill.payment_selection ?? '';
        vals.payment_amount = bill.payment_amount ?? '';
      }
    }
    setEditRowIdx(idx); setEditValues(vals);
  };
  const cancelRowEdit = () => { setEditRowIdx(null); setEditValues({}); };

  const saveRowEdit = async () => {
    if (editRowIdx === null || saving) return;
    const u = displayUtils[editRowIdx]; const bill = billsMap[u.bill_id] || {};
    const isPrimary = billFirstIdx[u.bill_id] === sortedUtils.indexOf(u);
    setSaving(true);
    try {
      const updates = [];
      for (const [field, newVal] of Object.entries(editValues)) {
        if (!EDITABLE_FIELDS.has(field)) continue;
        const isBillField = BILL_LEVEL_FIELDS.has(field);
        if (isBillField && !isPrimary) continue;
        const oldVal = isBillField ? bill[field] : u[field];
        const isNum = field !== 'gl_code' && field !== 'payment_selection';
        const parsedNew = isNum ? parseFloat(newVal) : newVal;
        const parsedOld = isNum ? parseFloat(oldVal) : oldVal;
        if (String(parsedNew) !== String(parsedOld ?? ''))
          updates.push({ field, newVal: String(newVal), parsedVal: parsedNew, isBillField });
      }
      if (!updates.length) { cancelRowEdit(); return; }
      for (const upd of updates) {
        const resp = await api.patch('/payments/v2/row-edit', {
          account_id: accountId, payment_file_id: pfId, utility_id: u.payment_file_utility_id,
          field_name: upd.field, new_value: upd.newVal, user_id: userId || '', user_name: userName || '',
          user_role: userRole || '', row_number: editRowIdx + 1,
        });
        if (upd.isBillField) setBillsMap(prev => ({ ...prev, [u.bill_id]: { ...prev[u.bill_id], [upd.field]: upd.parsedVal } }));
        else setAllUtils(prev => prev.map((row, i) => i === editRowIdx ? { ...row, [upd.field]: upd.parsedVal } : row));
        if (upd.field === 'pay_amount' && resp.data?.new_total_requested !== undefined) {
          setPf(prev => ({ ...prev, total_requested_amount: resp.data.new_total_requested }));
          fetchFundsPulled();
        }
        // When payment_selection changes, update the bill and utility values
        if (upd.field === 'payment_selection' && resp.data?.new_total_requested !== undefined) {
          setPf(prev => ({ ...prev, total_requested_amount: resp.data.new_total_requested }));
          if (resp.data?.new_payment_amount !== undefined) {
            setBillsMap(prev => ({ ...prev, [u.bill_id]: { ...prev[u.bill_id], payment_amount: resp.data.new_payment_amount } }));
            setAllUtils(prev => prev.map(row => row.bill_id === u.bill_id ? { ...row, pay_amount: resp.data.new_payment_amount } : row));
          }
          fetchFundsPulled();
        }
      }
      toast.success(`Updated ${updates.length} field${updates.length > 1 ? 's' : ''}`);
      const auditRes = await api.get(`/payments/v2/files/${pfId}/audit-log`, { params: { account_id: accountId } });
      setAuditLog(auditRes.data.logs || []);
      fetchChecks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); setEditRowIdx(null); setEditValues({}); }
  };

  // ── Revert ──
  const revertEntry = async (entryId) => {
    setRevertingId(entryId);
    try {
      await api.post('/payments/v2/revert-edit', { account_id: accountId, payment_file_id: pfId, audit_entry_id: entryId, user_id: userId || '', user_name: userName || '' });
      toast.success('Reverted');
      await fetchPfData(); fetchUtils(1); fetchChecks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to revert'); }
    finally { setRevertingId(null); }
  };

  const revertAll = async () => {
    setRevertingAll(true);
    try {
      const { data } = await api.post('/payments/v2/revert-all', { account_id: accountId, payment_file_id: pfId, user_id: userId || '', user_name: userName || '' });
      toast.success(`Reverted ${data.reverted_count} changes`);
      await fetchPfData(); fetchUtils(1); fetchChecks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to revert all'); }
    finally { setRevertingAll(false); }
  };

  // ── Remove row ──
  const removeRow = async () => {
    if (!removeConfirm) return;
    setRemovingId(removeConfirm.utilId);
    try {
      const { data: removeData } = await api.post('/payments/v2/remove-row', {
        account_id: accountId, payment_file_id: pfId, utility_id: removeConfirm.utilId,
        user_id: userId || '', user_name: userName || '', reason: 'Duplicate / manual removal',
      });
      toast.success('Row removed');
      setAllUtils(prev => prev.filter(u => u.payment_file_utility_id !== removeConfirm.utilId));
      setUtilTotal(prev => prev - 1);
      setRemoveConfirm(null);
      if (removeData?.new_total_requested !== undefined) {
        setPf(prev => ({ ...prev, total_requested_amount: removeData.new_total_requested }));
        fetchFundsPulled();
      }
      const auditRes = await api.get(`/payments/v2/files/${pfId}/audit-log`, { params: { account_id: accountId } });
      setAuditLog(auditRes.data.logs || []);
      fetchChecks();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to remove'); }
    finally { setRemovingId(null); }
  };

  // ── PDF ──
  const openPdf = async (billId) => {
    setPdfOpen(true); setPdfLoading(true); setPdfBase64(null); setPdfUrl(null); setPdfMessage(null);
    try {
      const { data } = await api.get(`/payments/v2/bill-pdf/${billId}`, { params: { account_id: accountId } });
      if (data.base64_data) setPdfBase64(data.base64_data);
      else if (data.pdf_url) setPdfUrl(data.pdf_url);
      else setPdfMessage(data.message || 'No PDF available for this bill');
    } catch { setPdfMessage('Failed to load PDF'); }
    finally { setPdfLoading(false); }
  };

  // ── Dismiss Issue ──
  const dismissIssue = async (utilityId, billId, scope = 'single') => {
    setDismissingId(utilityId || billId);
    try {
      await api.post('/payments/v2/dismiss-issue', {
        account_id: accountId, payment_file_id: pfId,
        utility_id: scope === 'single' ? utilityId : null,
        bill_id: billId, scope,
        user_id: userId || '', user_name: userName || '',
      });
      toast.success(scope === 'bill_group' ? 'Bill group dismissed' : 'Issue dismissed');
      fetchChecks();
      if (showIssuesOnly) fetchUtils(1);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to dismiss'); }
    finally { setDismissingId(null); }
  };

  const toggleIssueSelection = (utilId) => {
    setSelectedIssues(prev => {
      const next = new Set(prev);
      if (next.has(utilId)) next.delete(utilId);
      else next.add(utilId);
      return next;
    });
  };

  const toggleSelectAllIssues = () => {
    if (selectedIssues.size > 0) {
      setSelectedIssues(new Set());
    } else {
      const allIssue = new Set();
      for (const u of sortedUtils) {
        const uid = u.payment_file_utility_id;
        const hasFlag = (issueFlagsByUtil[uid] || []).length > 0;
        const hasDup = (dupInfo[uid] || []).length > 0;
        if (hasFlag || hasDup) allIssue.add(uid);
      }
      setSelectedIssues(allIssue);
    }
  };

  const bulkDismissSelected = async () => {
    if (selectedIssues.size === 0) return;
    setBulkDismissing(true);
    try {
      const utilIds = [...selectedIssues];
      // Collect unique bill_ids from selected utilities
      const billIds = [...new Set(
        sortedUtils
          .filter(u => selectedIssues.has(u.payment_file_utility_id))
          .map(u => u.bill_id)
          .filter(Boolean)
      )];
      await api.post('/payments/v2/dismiss-issues-bulk', {
        account_id: accountId, payment_file_id: pfId,
        utility_ids: utilIds, bill_ids: billIds,
        user_id: userId || '', user_name: userName || '',
      });
      toast.success(`${selectedIssues.size} issue(s) resolved`);
      setSelectedIssues(new Set());
      fetchChecks();
      if (showIssuesOnly) fetchUtils(1);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to resolve'); }
    finally { setBulkDismissing(false); }
  };

  const clearAllFilters = () => {
    setAcctFilter(''); setSupplierFilter('');
    setHasCreditFilter(false);
    if (showIssuesOnly) setShowIssuesOnly(false);
  };
  const hasActiveFilters = showIssuesOnly || acctFilter || supplierFilter || hasCreditFilter;

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchPfData(), fetchUtils(1), fetchChecks()]);
      toast.success('Refreshed');
    } catch { /* silent */ } finally { setRefreshing(false); }
  };

  // ── Actions ──
  const handleApprove = async () => {
    setActionLoading(true);
    try { await api.patch(`/payments/v2/files/${pfId}/approve`, null, { params: { account_id: accountId, user_id: userId, user_name: userName || '' } }); toast.success('Approved'); setApproveOpen(false); onActionComplete(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const handleMarkPaid = async () => {
    setActionLoading(true);
    try { await api.patch(`/payments/v2/files/${pfId}/mark-paid`, null, { params: { account_id: accountId, user_id: userId, user_name: userName || '' } }); toast.success('Marked as paid'); setMarkPaidOpen(false); onActionComplete(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setActionLoading(false); }
  };

  const downloadAuditCSV = () => {
    const headers = ['Row #', 'Timestamp', 'User', 'Action', 'Column', 'Old Value', 'New Value'];
    const rows = auditLog.map(log => [
      log.details?.row_number || '', fmtDT(log.created_at), log.user_name || '',
      log.action || '', FIELD_DISPLAY[log.details?.field_name] || log.details?.field_name || '',
      log.details?.old_value ?? '', log.details?.new_value ?? '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `audit_log_${pf?.batch_name || pfId}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const sCfg = STATUS_CFG[pf?.status] || STATUS_CFG.review;
  const SIcon = sCfg.icon;
  const isPaid = pf?.status === 'paid';
  const isReview = pf?.status === 'review';
  const isApproved = pf?.status === 'approved';
  const isSuperRole = ['admin', 'super_admin'].includes(userRole);
  const isOpsRole = ['ops_admin', 'ops_editor'].includes(userRole);
  const canEdit = isReview || (isApproved && isSuperRole) || isOpsRole;
  const canSeeIssues = isSuperRole || isOpsRole || isReview;
  const canEditApprovedLimited = isApproved && !isSuperRole && ['admin', 'editor'].includes(userRole);
  const hasRowEdits = auditLog.some(l => l.action === 'row_edit');
  const pdfDataUrl = pdfBase64 ? `data:application/pdf;base64,${pdfBase64}` : null;
  const totalDupes = duplicates.counts?.total || 0;
  const totalIssues = issueSummary?.total_issues ?? issueUtilIds.size;

  if (loading) return (
    <div className="h-screen bg-background flex flex-col" data-testid="pf-detail-loading">
      <div className="p-4 border-b"><Skeleton className="h-10 w-72" /></div>
      <div className="p-4 flex gap-4"><Skeleton className="h-16 w-48" /><Skeleton className="h-16 w-48" /><Skeleton className="h-16 w-48" /></div>
      <div className="flex-1 p-4"><Skeleton className="h-full" /></div>
    </div>
  );

  if (!pf) return (
    <div className="h-screen bg-background flex items-center justify-center">
      <div className="text-center text-muted-foreground">
        <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-40" />
        <p className="text-sm mb-4">Payment file not found</p>
        <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={200}>
    <div className="h-screen bg-background flex flex-col overflow-hidden" data-testid="pf-detail-page">
      {/* Header */}
      <div className="border-b border-border bg-card px-4 py-2.5 flex-shrink-0" data-testid="pf-detail-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={onBack} data-testid="pf-detail-back"><ArrowLeft className="h-5 w-5" /></Button>
            <h1 className="text-lg font-bold truncate">{pf.batch_name || `PF-${pf.batch_id}`}</h1>
            <Badge variant="outline" className={`${sCfg.color} text-xs font-medium`}><SIcon className="h-3 w-3 mr-1" />{sCfg.label}</Badge>
            <span className="text-xs text-muted-foreground hidden md:inline">
              Generated: {fmtD(pf.generated_at)}{pf.approved_at && <> | Approved: {fmtDT(pf.approved_at)}</>}{pf.paid_at && <> | Paid: {fmtDT(pf.paid_at)}</>}
            </span>
          </div>
          <div className="flex items-center gap-2" data-testid="pf-detail-actions">
            {canSeeIssues && (issueSummary?.total_mismatches ?? 0) > 0 && (
              <Badge variant="outline" className="bg-orange-500/10 text-orange-600 border-orange-500/20 text-xs" data-testid="validation-flag-badge">
                <AlertTriangle className="h-3 w-3 mr-1" />{issueSummary.total_mismatches} mismatch{issueSummary.total_mismatches > 1 ? 'es' : ''}
              </Badge>
            )}
            {canSeeIssues && totalDupes > 0 && (
              <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/20 text-xs" data-testid="duplicate-badge">
                <Copy className="h-3 w-3 mr-1" />{totalDupes} duplicate{totalDupes > 1 ? 's' : ''}
              </Badge>
            )}
            {canSeeIssues && issueSummary?.blank_address > 0 && (
              <Badge variant="outline" className="bg-yellow-500/10 text-yellow-700 border-yellow-500/20 text-xs" data-testid="blank-addr-badge">
                <AlertTriangle className="h-3 w-3 mr-1" />{issueSummary.blank_address} blank addr
              </Badge>
            )}
            {canSeeIssues && issueSummary?.address_mismatch > 0 && (
              <Badge variant="outline" className="bg-cyan-500/10 text-cyan-700 border-cyan-500/20 text-xs" data-testid="addr-mismatch-badge">
                <AlertTriangle className="h-3 w-3 mr-1" />{issueSummary.address_mismatch} addr mismatch
              </Badge>
            )}
            {canSeeIssues && issueSummary?.blank_gl > 0 && (
              <Badge variant="outline" className="bg-pink-500/10 text-pink-700 border-pink-500/20 text-xs" data-testid="blank-gl-badge">
                <AlertTriangle className="h-3 w-3 mr-1" />{issueSummary.blank_gl} blank GL
              </Badge>
            )}
            {canSeeIssues && issueSummary?.child_duplicates > 0 && (
              <Badge variant="outline" className="bg-violet-500/10 text-violet-700 border-violet-500/20 text-xs" data-testid="child-dup-badge">
                <Copy className="h-3 w-3 mr-1" />{issueSummary.child_duplicates} child dup
              </Badge>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8 relative" onClick={() => setAuditOpen(true)} data-testid="pf-audit-btn">
              <History className="h-4 w-4" />
              {auditLog.length > 0 && <span className="absolute -top-0.5 -right-0.5 bg-blue-600 text-white text-[9px] rounded-full h-4 w-4 flex items-center justify-center">{auditLog.length}</span>}
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={downloadCsv} title="Download as CSV" data-testid="pf-download-csv-btn">
              <Download className="h-4 w-4" />
            </Button>
            {isReview && <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setApproveOpen(true)} data-testid="pf-approve-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve</Button>}
            {isApproved && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setMarkPaidOpen(true)} data-testid="pf-mark-paid-btn"><Banknote className="h-4 w-4 mr-1.5" /> Mark as Paid</Button>}
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="border-b border-border bg-muted/30 px-4 py-1.5 flex-shrink-0 flex flex-col gap-1.5" data-testid="pf-stats-bar">
        <div className="flex items-center gap-8 text-sm">
          {detailPmtStats && (isApproved || isPaid) && (
            <Stat label="Paid Invoices" value={`${detailPmtStats.paid_bills || 0}/${detailPmtStats.total_bills || 0} (${detailPmtStats.pct_paid || 0}%)`} data-testid="paid-invoices-stat" />
          )}
          {!(isApproved || isPaid) && <Stat label="Invoices" value={invoiceCount} />}
          <Stat label={(isApproved || isPaid) ? 'Approved Amount' : 'Total Funds Requested'} value={fmtC(pf.total_requested_amount)} highlight />
          {(isApproved || isPaid) && (
            <Stat label="Funds Pulled" value={fmtC(fundsTotalPulled)} />
          )}
          {(isApproved || isPaid) && detailPmtStats && detailPmtStats.total_paid > 0 && detailPmtStats.invoice_variance !== 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Stat label="Paid Variance" value={fmtC(detailPmtStats.invoice_variance)} />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs p-3 max-w-[260px]">
                <p className="font-semibold mb-1">Paid vs Requested Difference</p>
                <p className="text-muted-foreground">Sum of (requested - paid) for all paid invoices. Negative means underpaid.</p>
              </TooltipContent>
            </Tooltip>
          )}
          {(isApproved || isPaid) && detailPmtStats && (
            <Stat label="Total Paid" value={fmtC(detailPmtStats?.total_paid ?? 0)} data-testid="total-paid-stat" />
          )}
          {detailPmtStats && (detailPmtStats.total_refunded > 0 || detailPmtStats.total_to_refund > 0) && (
            <Tooltip>
              <TooltipTrigger>
                <div className="flex flex-col items-start gap-0.5 px-3 py-1.5 rounded-md bg-red-500/10" data-testid="refund-split-stat">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Refunds</span>
                  <div className="flex items-baseline gap-3 text-xs">
                    <span className="text-amber-600 font-semibold" data-testid="stat-to-refund">To Refund: {fmtC(detailPmtStats.total_to_refund || 0)}</span>
                    <span className="text-emerald-600 font-semibold" data-testid="stat-refunded">Refunded: {fmtC(detailPmtStats.total_refunded || 0)}</span>
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs p-3 max-w-[280px]">
                <p className="font-semibold mb-1">Refund Breakdown</p>
                <p className="text-muted-foreground"><strong>To Refund</strong>: refund recorded but not yet processed.<br/><strong>Refunded</strong>: refund actually completed.</p>
              </TooltipContent>
            </Tooltip>
          )}
          {(isApproved || isPaid) && detailPmtStats && detailPmtStats.unpaid_bills > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Stat label="Unpaid" value={fmtC(detailPmtStats.unpaid_requested)} highlight />
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs p-3 max-w-[280px]">
                <p className="font-semibold mb-1.5">Unpaid Breakdown</p>
                <div className="space-y-1">
                  <div className="flex justify-between gap-4"><span>Unpaid Invoices</span><span className="font-mono">{detailPmtStats.unpaid_bills}</span></div>
                  <div className="flex justify-between gap-4"><span>Unpaid Requested Amount</span><span className="font-mono font-semibold">{fmtC(detailPmtStats.unpaid_requested)}</span></div>
                  {detailPmtStats.invoice_variance !== 0 && (
                    <div className="flex justify-between gap-4 border-t pt-1 text-muted-foreground"><span>Paid Invoice Variance</span><span className="font-mono">{fmtC(detailPmtStats.invoice_variance)}</span></div>
                  )}
                  <div className="flex justify-between gap-4 border-t pt-1 font-semibold"><span>Net Outstanding</span><span className="font-mono">{fmtC(
                    (detailPmtStats.unpaid_requested || 0) + (detailPmtStats.invoice_variance || 0)
                  )}</span></div>
                </div>
              </TooltipContent>
            </Tooltip>
          )}
          {detailPmtStats && detailPmtStats.total_svc_fee > 0 && (
            <Stat label="Service Fees" value={fmtC(detailPmtStats.total_svc_fee)} />
          )}
          {detailPmtStats && detailPmtStats.missing_txn_ref > 0 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20 text-xs cursor-pointer" data-testid="missing-txn-badge" onClick={() => setPaidFilter('missing_txn')}>
                  <AlertTriangle className="h-3 w-3 mr-1" />{detailPmtStats.missing_txn_ref} missing txn ref
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="text-xs">Click to filter to invoices with missing transaction references</TooltipContent>
            </Tooltip>
          )}
          <div className="ml-auto flex items-center gap-2">
            {(isApproved || isPaid) && canManageFunds && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setFundsPulledOpen(true)}
                data-testid="funds-pulled-toggle-btn"
              >
                <DollarSign className="h-3 w-3 mr-1" />
                Funds Pulled ({fundsPulled.length})
              </Button>
            )}
            {canSeeIssues && selectedIssues.size > 0 && canEdit && (
              <Button
                size="sm"
                className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                onClick={bulkDismissSelected}
                disabled={bulkDismissing}
                data-testid="bulk-resolve-btn"
              >
                {bulkDismissing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ShieldOff className="h-3 w-3 mr-1" />}
                Resolve Selected ({selectedIssues.size})
              </Button>
            )}
            {canSeeIssues && totalIssues > 0 && canEdit && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs text-muted-foreground border-muted-foreground/30"
                onClick={toggleSelectAllIssues}
                data-testid="select-all-issues-btn"
              >
                {selectedIssues.size > 0 ? 'Deselect All' : 'Select All Issues'}
              </Button>
            )}
            {canSeeIssues && totalIssues > 0 && (
              <Button
                variant={showIssuesOnly ? 'default' : 'outline'}
                size="sm"
                className={`h-7 text-xs ${showIssuesOnly ? 'bg-red-600 hover:bg-red-700 text-white' : 'text-red-600 border-red-300 hover:bg-red-50'}`}
                onClick={() => setShowIssuesOnly(p => !p)}
                data-testid="filter-issues-btn"
              >
                <AlertTriangle className="h-3 w-3 mr-1" />
                {showIssuesOnly ? `Showing Issues (${utilTotal})` : `Filter Issues (${totalIssues})`}
              </Button>
            )}
            {canSeeIssues && crossPfBillIds.size > 0 && (
              <Badge className="bg-red-600 text-white text-[10px] font-bold animate-pulse" data-testid="cross-pf-alert-badge">
                {crossPfBillIds.size} Cross-PF Duplicate{crossPfBillIds.size > 1 ? 's' : ''}
              </Badge>
            )}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={clearAllFilters} data-testid="clear-filters-btn">
                <X className="h-3 w-3 mr-1" /> Clear Filters
              </Button>
            )}
            <span className="text-xs text-muted-foreground">
              {allUtils.length} of {utilTotal}
              {hasMore && <span className="ml-1 text-blue-600">(scroll for more)</span>}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-invoices-btn"
              title="Refresh invoices"
            >
              <RotateCcw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        {/* Column Filters Row */}
        <div className="flex items-center gap-2 pb-0.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><Search className="h-3 w-3" />Filters:</span>
          <div className="relative">
            <input
              type="text"
              value={acctFilter}
              onChange={e => setAcctFilter(e.target.value)}
              placeholder="Search account #..."
              className="h-6 w-40 text-xs border rounded px-2 bg-background"
              data-testid="filter-account-number"
              list="acct-options"
            />
            <datalist id="acct-options">
              {distinctAcctNos.map(a => <option key={a} value={a} />)}
            </datalist>
          </div>
          <select
            value={supplierFilter}
            onChange={e => setSupplierFilter(e.target.value)}
            className="h-6 w-40 text-xs border rounded px-1 bg-background"
            data-testid="filter-supplier"
          >
            <option value="">All Suppliers</option>
            {distinctSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <Button
            variant={hasCreditFilter ? 'default' : 'outline'}
            size="sm"
            className={`h-6 text-[10px] px-2 ${hasCreditFilter ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}`}
            onClick={() => setHasCreditFilter(p => !p)}
            data-testid="filter-credit"
          >
            Credits
          </Button>
          {(isApproved || isPaid) && (
            <select
              value={paidFilter}
              onChange={e => setPaidFilter(e.target.value)}
              className="h-6 w-36 text-xs border rounded px-1 bg-background"
              data-testid="filter-paid-status"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
              <option value="missing_txn">Missing Txn</option>
              <option value="with_variance">With Variance</option>
              <option value="without_variance">Without Variance</option>
              <option value="with_service_charge">With Svc Charge</option>
              <option value="without_service_charge">Without Svc Charge</option>
            </select>
          )}
          {!showMoreFilters && (isApproved || isPaid) && (
            <button
              className="h-6 text-[10px] text-blue-600 hover:text-blue-800 font-medium px-1"
              onClick={() => setShowMoreFilters(true)}
              data-testid="show-more-filters"
            >
              More...
            </button>
          )}
        </div>
        {showMoreFilters && (isApproved || isPaid) && (
          <div className="flex items-center gap-2 pb-0.5 flex-wrap" data-testid="more-filters-row">
            <span className="text-[10px] text-muted-foreground w-[52px]"></span>
            <div className="relative">
              <input
                type="text"
                value={paidByFilter}
                onChange={e => setPaidByFilter(e.target.value)}
                placeholder="Paid by..."
                className="h-6 w-40 text-xs border rounded px-2 bg-background"
                data-testid="filter-paid-by"
                list="paid-by-options"
              />
              <datalist id="paid-by-options">
                {distinctPaidBy.map(p => <option key={p} value={p} />)}
              </datalist>
            </div>
            <select
              value={txnCountFilter}
              onChange={e => setTxnCountFilter(e.target.value)}
              className="h-6 w-36 text-xs border rounded px-1 bg-background"
              data-testid="filter-txn-count"
            >
              <option value="">Transactions</option>
              <option value="single">Single</option>
              <option value="multi">Multi-Payment</option>
            </select>
            <button
              className="h-6 text-[10px] text-muted-foreground hover:text-foreground px-1"
              onClick={() => { setShowMoreFilters(false); setPaidByFilter(''); setTxnCountFilter(''); }}
              data-testid="hide-more-filters"
            >
              Less
            </button>
          </div>
        )}
      </div>

      {/* Funds Pulled Modal */}
      <Dialog open={fundsPulledOpen} onOpenChange={setFundsPulledOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="funds-pulled-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4" />
              Funds Pulled — {pf.batch_name}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-4 text-xs pt-1">
              <span>Approved: <strong>{fmtC(pf.total_requested_amount)}</strong></span>
              <span>Pulled: <strong className="text-blue-600">{fmtC(fundsTotalPulled)}</strong></span>
              <span>Remaining: <strong className={fundsRemaining > 0 ? 'text-amber-600' : 'text-emerald-600'}>{fmtC(fundsRemaining)}</strong></span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Auto Generate & Nacha Toolbar */}
            <div className="flex items-center gap-2 flex-wrap border-b border-border pb-3">
              {fundsRemaining > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  className="h-8 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={loadAutoGenPreview}
                  disabled={autoGenLoading}
                  data-testid="auto-generate-btn"
                >
                  {autoGenLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                  Auto Generate
                </Button>
              )}
              {nachaFiles.length > 0 && (
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={regenerateNacha} disabled={nachaRegenerating} data-testid="regenerate-nacha-btn">
                  <RefreshCw className={`h-3 w-3 mr-1 ${nachaRegenerating ? 'animate-spin' : ''}`} />
                  Regenerate Nacha
                </Button>
              )}
              {nachaFiles.length > 0 && (
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => downloadNacha(nachaFiles[0].id)} data-testid="download-nacha-btn">
                  <Download className="h-3 w-3 mr-1" />
                  Download .ACH ({nachaFiles[0].entry_count} entries)
                </Button>
              )}
            </div>

            {/* Auto Generate Preview Table */}
            {autoGenPreview && (
              <div className="border border-violet-200 rounded-md bg-violet-50/30 p-3 space-y-3" data-testid="auto-gen-preview">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-violet-700 flex items-center gap-1">
                    <Zap className="h-3.5 w-3.5" /> Auto-Generated Fund Pulls Preview
                  </h4>
                  <span className="text-xs text-muted-foreground">
                    Total: <strong className="text-violet-600">{fmtC(autoGenEntries.filter(e => e.included).reduce((s, e) => s + e.amount, 0))}</strong>
                  </span>
                </div>
                <div className="overflow-x-auto border border-violet-200 rounded-md">
                  <Table>
                    <TableHeader><TableRow className="bg-violet-100/50">
                      <TableHead className="text-[10px] uppercase font-semibold w-8"></TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold">Legal Entity</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold w-24">EIN</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold w-20 text-right">Lines</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold w-28 text-right">Amount</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold w-40">Txn Reference</TableHead>
                      <TableHead className="text-[10px] uppercase font-semibold w-16">ACH</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {autoGenEntries.map((entry, i) => (
                        <TableRow key={i} className={`text-xs ${!entry.included ? 'opacity-50' : ''}`}>
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={entry.included}
                              onChange={() => setAutoGenEntries(prev => prev.map((e, j) => j === i ? { ...e, included: !e.included } : e))}
                              className="h-3 w-3"
                            />
                          </TableCell>
                          <TableCell className="text-xs truncate max-w-[200px]" title={entry.legal_entity_name}>{entry.legal_entity_name}</TableCell>
                          <TableCell className="font-mono text-xs">{entry.ein || '\u2014'}</TableCell>
                          <TableCell className="text-right font-mono">{entry.line_count}</TableCell>
                          <TableCell className="text-right font-mono font-medium text-blue-600">{fmtC(entry.amount)}</TableCell>
                          <TableCell>
                            <Input
                              className="h-6 text-xs w-36"
                              value={entry.transaction_reference}
                              onChange={(e) => setAutoGenEntries(prev => prev.map((en, j) => j === i ? { ...en, transaction_reference: e.target.value } : en))}
                            />
                          </TableCell>
                          <TableCell>
                            {entry.has_ach ? (
                              <Badge className="text-[9px] bg-green-100 text-green-700 rounded-sm">ACH</Badge>
                            ) : (
                              <Badge variant="outline" className="text-[9px] rounded-sm text-amber-600 border-amber-300">None</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <div className="text-[10px] text-muted-foreground">
                    {autoGenEntries.filter(e => !e.has_ach && e.included).length > 0 && (
                      <span className="text-amber-600">
                        {autoGenEntries.filter(e => !e.has_ach && e.included).length} entities without ACH — Nacha will skip these
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => { setAutoGenPreview(null); setAutoGenEntries([]); }}>Cancel</Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs border-violet-300 text-violet-700 hover:bg-violet-50"
                      onClick={previewAutoGenNacha}
                      disabled={autoGenNachaPreviewing || autoGenEntries.filter(e => e.included).length === 0}
                      data-testid="preview-nacha-btn"
                      title="Download a NACHA file for the selected entries without creating fund pulls"
                    >
                      {autoGenNachaPreviewing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Download className="h-3 w-3 mr-1" />}
                      Preview Nacha ({autoGenEntries.filter(e => e.included).length})
                    </Button>
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                      onClick={submitAutoGenFunds}
                      disabled={autoGenSubmitting || autoGenEntries.filter(e => e.included).length === 0}
                      data-testid="submit-auto-gen-btn"
                    >
                      {autoGenSubmitting ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                      Submit {autoGenEntries.filter(e => e.included).length} Pulls & Generate Nacha
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {fundsPulled.length > 0 ? (
              <div className="overflow-x-auto border rounded-md">
                <Table data-testid="funds-pulled-table">
                  <TableHeader><TableRow className="bg-muted/30">
                    <TableHead className="text-[10px] uppercase font-semibold w-36">Date / User</TableHead>
                    <TableHead className="text-[10px] uppercase font-semibold w-28">Amount</TableHead>
                    <TableHead className="text-[10px] uppercase font-semibold w-24">Status</TableHead>
                    <TableHead className="text-[10px] uppercase font-semibold w-32">Txn Reference</TableHead>
                    <TableHead className="text-[10px] uppercase font-semibold">LLC / Legal Entity</TableHead>
                    <TableHead className="text-[10px] uppercase font-semibold w-24">EIN</TableHead>
                    <TableHead className="text-[10px] uppercase font-semibold">Notes</TableHead>
                    <TableHead className="text-[10px] uppercase font-semibold w-10"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {fundsPulled.map((entry) => (
                      <TableRow key={entry.id} className="text-xs">
                        <TableCell>
                          <div className="text-muted-foreground text-[10px]">{fmtDT(entry.created_at)}</div>
                          <div className="text-[10px] font-medium">{entry.user_name || '\u2014'}</div>
                        </TableCell>
                        <TableCell className="font-mono font-medium text-blue-600">{fmtC(entry.amount)}</TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="flex items-center gap-1 cursor-help">
                                  {(entry.status || 'initiated') === 'pulled' ? (
                                    <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-300 rounded-sm" data-testid={`fund-status-${entry.id}`}>Pulled</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-[9px] rounded-sm text-amber-600 border-amber-300" data-testid={`fund-status-${entry.id}`}>
                                      Initiated
                                      <Info className="h-2.5 w-2.5 ml-0.5" />
                                    </Badge>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-[220px] text-xs">
                                {(entry.status || 'initiated') === 'pulled'
                                  ? 'Funds have been received and applied to bills.'
                                  : 'ACH initiated. It could take 24hrs to 3 business days to receive the funds.'}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{entry.transaction_reference || '\u2014'}</TableCell>
                        <TableCell className="text-xs truncate max-w-[180px]" title={entry.legal_entity_name}>{entry.legal_entity_name || '\u2014'}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{entry.ein || '\u2014'}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{entry.notes || '\u2014'}</TableCell>
                        <TableCell>
                          <button
                            onClick={() => deleteFundEntry(entry.id)}
                            disabled={deletingFundId === entry.id}
                            className="text-red-500 hover:text-red-700 p-0.5 rounded hover:bg-red-50"
                            data-testid={`delete-fund-${entry.id}`}
                          >
                            {deletingFundId === entry.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-4 text-center">No funds pulled yet. Add the first entry below.</p>
            )}

            {/* Add Fund Entry Form */}
            <div className="border rounded-lg p-4 bg-muted/20 space-y-3">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Record Fund Pull</h4>
              <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                <div><span className="font-medium text-foreground">Date/Time:</span> {new Date().toLocaleString()}</div>
                <div><span className="font-medium text-foreground">User:</span> {userName || 'Unknown'}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">Amount *</label>
                  <Input
                    type="number"
                    placeholder={`Max: ${fmtC(fundsRemaining)}`}
                    value={newFundAmount}
                    onChange={e => setNewFundAmount(e.target.value)}
                    className="h-8 text-xs"
                    step="0.01"
                    min="0.01"
                    max={fundsRemaining}
                    data-testid="fund-amount-input"
                  />
                  {newFundAmount && parseFloat(newFundAmount) > fundsRemaining && (
                    <p className="text-[10px] text-red-500 mt-0.5">Exceeds remaining ({fmtC(fundsRemaining)})</p>
                  )}
                </div>
                <div>
                  <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">Transaction Reference # *</label>
                  <Input
                    type="text"
                    placeholder="e.g. TXN-2026-001"
                    value={newFundTxnRef}
                    onChange={e => setNewFundTxnRef(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="fund-txn-ref-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">LLC / Legal Entity</label>
                  <select
                    value={newFundLlc}
                    onChange={e => setNewFundLlc(e.target.value)}
                    className="h-8 w-full text-xs border border-input rounded-md px-2 bg-background"
                    data-testid="fund-llc-select"
                  >
                    <option value="">Select LLC / Legal Entity</option>
                    {llcOptions.map((opt, i) => (
                      <option key={i} value={`${opt.legal_entity_name}|${opt.ein}`}>
                        {opt.legal_entity_name} {opt.ein ? `(EIN: ${opt.ein})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-muted-foreground uppercase mb-1 block">Notes</label>
                  <Input
                    type="text"
                    placeholder="Optional notes"
                    value={newFundNotes}
                    onChange={e => setNewFundNotes(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="fund-notes-input"
                  />
                </div>
              </div>
              <div className="flex justify-end pt-1">
                <Button
                  size="sm"
                  className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white px-6"
                  onClick={addFundsPulled}
                  disabled={addingFund || !newFundAmount || !newFundTxnRef.trim() || (parseFloat(newFundAmount) > fundsRemaining)}
                  data-testid="add-fund-btn"
                >
                  {addingFund ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                  Record Fund Pull
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Line-Item Transaction Drawer */}
      <Sheet open={txnDrawerOpen} onOpenChange={(o) => { setTxnDrawerOpen(o); if (!o) { setTxnUtil(null); setBillTxns([]); setShowAddTxnForm(false); setEditingTxnId(null); } }}>
        <SheetContent side="right" className="sm:max-w-[440px] w-[440px] flex flex-col p-0" data-testid="txn-drawer">
          <SheetHeader className="px-6 pt-6 pb-3">
            <SheetTitle className="flex items-center gap-2 text-sm">
              <CreditCard className="h-4 w-4" />
              {txnForm._locked && !showAddTxnForm ? 'Payment Details' : (showAddTxnForm ? 'Record Payment' : 'Payment Details')}
            </SheetTitle>
            {txnUtil && (
              <div className="text-xs text-muted-foreground space-y-0.5 pt-1">
                <p><strong>{txnUtil.supplier_name}</strong></p>
                <p>Invoice: {txnUtil.invoice_number || '\u2014'} &bull; Requested: {fmtC(billsMap[txnUtil.bill_id]?.payment_amount ?? txnUtil.pay_amount)}</p>
                {txnUtil.legal_entity_name && <p>Entity: {txnUtil.legal_entity_name}</p>}
              </div>
            )}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* LOCKED VIEW: Show existing payment details read-only */}
            {txnForm._locked && !showAddTxnForm && (
              <div className="space-y-4" data-testid="txn-locked-view">
                <div className="bg-muted/40 border border-border rounded-lg p-4 space-y-3">
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Transaction Reference</label>
                    <p className="text-sm font-mono font-medium mt-0.5">{txnForm.transaction_reference || '\u2014'}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Paid Amount</label>
                      <p className="text-sm font-mono font-bold text-emerald-700 mt-0.5">{fmtC(txnForm.amount)}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Service Fee</label>
                      <p className="text-sm font-mono mt-0.5">{txnForm.payment_service_charge ? fmtC(txnForm.payment_service_charge) : '\u2014'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Payment Method</label>
                      <p className="text-sm mt-0.5 capitalize">{txnForm.payment_method || '\u2014'}</p>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Transaction Date</label>
                      <p className="text-sm mt-0.5">{txnForm.transaction_date ? fmtD(txnForm.transaction_date) : '\u2014'}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Paid By</label>
                    <p className="text-sm font-medium mt-0.5">{txnForm.paid_by_name || '\u2014'}</p>
                  </div>
                  {txnForm.notes && (
                    <div>
                      <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Notes</label>
                      <p className="text-sm text-muted-foreground mt-0.5">{txnForm.notes}</p>
                    </div>
                  )}
                </div>

                {/* Transaction history from pm_payment_file_transactions */}
                {billTxnsLoading ? (
                  <div className="flex items-center justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>
                ) : billTxns.length > 1 ? (
                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Transaction History ({billTxns.length})</p>
                    {billTxns.map((t, i) => (
                      <div key={t.id || i} className="border border-border rounded p-2.5 bg-background text-xs flex items-center justify-between" data-testid={`txn-history-${i}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-muted-foreground font-mono text-[10px]">#{i + 1}</span>
                          <span className="font-bold font-mono text-emerald-700">{fmtC(t.amount)}</span>
                          <span className="text-muted-foreground truncate" title={t.transaction_reference}>{t.transaction_reference || '\u2014'}</span>
                        </div>
                        <span className="text-muted-foreground shrink-0">{t.paid_by_name || '\u2014'}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {/* EDITABLE FORM: For new payment or editing */}
            {showAddTxnForm && (
              <div className="space-y-4" data-testid="add-txn-form">
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Transaction Reference *</label>
                  <Input className="h-9 text-sm font-mono" value={txnForm.transaction_reference} onChange={e => setTxnForm(prev => ({ ...prev, transaction_reference: e.target.value }))} placeholder="e.g. CHK-2026-001" data-testid="txn-ref-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Paid Amount *</label>
                    <Input type="number" className="h-9 text-sm font-mono" value={txnForm.amount} onChange={e => setTxnForm(prev => ({ ...prev, amount: e.target.value }))} step="0.01" data-testid="txn-amount-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Service Fee</label>
                    <Input type="number" className="h-9 text-sm font-mono" value={txnForm.payment_service_charge} onChange={e => setTxnForm(prev => ({ ...prev, payment_service_charge: e.target.value }))} step="0.01" placeholder="0.00" data-testid="txn-service-charge-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Payment Method</label>
                    <Select value={txnForm.payment_method} onValueChange={v => setTxnForm(prev => ({ ...prev, payment_method: v }))}>
                      <SelectTrigger className="h-9 text-sm" data-testid="txn-payment-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="ach">ACH</SelectItem>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Transaction Date</label>
                    <Input type="date" className="h-9 text-sm" value={txnForm.transaction_date || ''} onChange={e => setTxnForm(prev => ({ ...prev, transaction_date: e.target.value }))} data-testid="txn-date-input" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Paid By *</label>
                  <Select value={txnForm.paid_by_id || userId} onValueChange={v => {
                    const u = orgUsers.find(ou => ou.id === v);
                    setTxnForm(prev => ({ ...prev, paid_by_id: v, paid_by_name: u?.name || prev.paid_by_name }));
                  }}>
                    <SelectTrigger className="h-9 text-sm" data-testid="txn-paid-by"><SelectValue placeholder="Select user" /></SelectTrigger>
                    <SelectContent>
                      {orgUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}
                      {orgUsers.length === 0 && <SelectItem value={userId}>{userName} (current)</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Notes</label>
                  <textarea className="w-full min-h-[70px] p-2 text-sm border border-input bg-background rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-ring" value={txnForm.notes} onChange={e => setTxnForm(prev => ({ ...prev, notes: e.target.value }))} placeholder="Payment notes..." data-testid="txn-notes-input" />
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-border px-6 py-3 flex items-center gap-2 justify-between">
            <div>
              {txnForm._locked && !showAddTxnForm && (
                <Button variant="outline" size="sm" className="text-xs" onClick={() => { setShowAddTxnForm(true); setTxnForm(prev => ({ ...prev, _locked: false })); }} data-testid="edit-txn-btn">
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
              )}
              {showAddTxnForm && txnForm._locked === false && editingTxnId && (
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => { setEditingTxnId(null); setShowAddTxnForm(false); setTxnForm(prev => ({ ...prev, _locked: true })); }}>Cancel</Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setTxnDrawerOpen(false)}>Close</Button>
              {showAddTxnForm && (
                <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={saveTxn} disabled={txnSaving} data-testid="save-txn-btn">
                  {txnSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                  {editingTxnId ? 'Update' : 'Save'} Payment
                </Button>
              )}
              {txnForm._locked && !showAddTxnForm && (
                <Button variant="outline" size="sm" className="text-xs" onClick={() => {
                  setShowAddTxnForm(true);
                  const bill = billsMap[txnUtil?.bill_id] || {};
                  const totalReq = parseFloat(bill.payment_amount ?? txnUtil?.pay_amount ?? 0);
                  const existingPaid = parseFloat(txnForm.amount || 0);
                  const rem = Math.max(0, totalReq - existingPaid);
                  setTxnForm({ transaction_reference: '', amount: rem > 0 ? rem : '', payment_service_charge: '', payment_method: '', notes: '', transaction_date: new Date().toISOString().split('T')[0], _totalRequested: totalReq, paid_by_id: userId, paid_by_name: userName, _locked: false });
                  setEditingTxnId(null);
                }} data-testid="add-another-txn-btn">
                  <Plus className="h-3 w-3 mr-1" /> Add Transaction
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Refund Drawer — From/To pattern */}
      <Sheet open={refundDrawerOpen} onOpenChange={setRefundDrawerOpen}>
        <SheetContent side="right" className="sm:max-w-[440px] w-[440px] flex flex-col p-0" data-testid="refund-drawer">
          <SheetHeader className="px-6 pt-6 pb-3">
            <SheetTitle className="flex items-center gap-2 text-sm">
              <ArrowRightLeft className="h-4 w-4 text-red-600" />
              {refundForm._locked ? 'Refund Details' : (refundForm.isEdit ? 'Edit Refund' : 'Record Refund')}
            </SheetTitle>
            {refundUtil && (
              <div className="text-xs text-muted-foreground space-y-0.5 pt-1">
                <p><strong>{refundUtil.supplier_name}</strong></p>
                <p>Invoice: {refundUtil.invoice_number || '\u2014'} &bull; Paid: <span className="font-mono font-semibold text-emerald-700">{fmtC(txnMap[refundUtil?.payment_file_utility_id]?.paid_amount || refundUtil.paid_amount)}</span></p>
              </div>
            )}
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* LOCKED VIEW */}
            {refundForm._locked && (
              <div className="bg-red-50/30 border border-red-200 rounded-lg p-4 space-y-3" data-testid="refund-locked-view">
                <div>
                  <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Refund Amount</label>
                  <p className="text-sm font-mono font-bold text-red-700 mt-0.5">{fmtC(refundForm.refund_amount)}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">From</label>
                    <p className="text-sm font-medium mt-0.5">{refundForm.refunded_by || 'BillPortal'}</p>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">To</label>
                    <p className="text-sm font-medium mt-0.5">{refundForm.refund_to || orgName || '\u2014'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Reference</label>
                    <p className="text-sm font-mono mt-0.5">{refundForm.refund_reference || '\u2014'}</p>
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Refund Date</label>
                    <p className="text-sm mt-0.5">{refundForm.refund_date ? fmtD(refundForm.refund_date) : '\u2014'}</p>
                  </div>
                </div>
                {refundForm.refund_method && (
                  <div>
                    <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Method / Notes</label>
                    <p className="text-sm text-muted-foreground mt-0.5 capitalize">{refundForm.refund_method}</p>
                  </div>
                )}
                {/* Refund Status (locked view) */}
                <div className="pt-2 border-t border-border">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${refundForm.refund_completed ? 'bg-emerald-500/15 text-emerald-700 border border-emerald-500/20' : 'bg-amber-500/15 text-amber-700 border border-amber-500/20'}`} data-testid="refund-status-badge">
                    {refundForm.refund_completed
                      ? <><CheckCircle2 className="h-3 w-3" /> Refunded</>
                      : <><Clock className="h-3 w-3" /> To Refund</>}
                  </span>
                </div>
              </div>
            )}
            {/* EDITABLE FORM */}
            {!refundForm._locked && (
              <div className="space-y-4" data-testid="refund-form">
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Refund Amount *</label>
                  <Input type="number" className="h-9 text-sm font-mono" value={refundForm.refund_amount} onChange={e => setRefundForm(prev => ({ ...prev, refund_amount: e.target.value }))} step="0.01" placeholder="0.00" data-testid="refund-amount-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">From (Refunded By)</label>
                    <Select value={refundForm.refunded_by_id || 'billportal'} onValueChange={v => {
                      setRefundForm(prev => ({ ...prev, refunded_by_id: v, refunded_by: v === 'billportal' ? 'BillPortal' : v === 'anchor' ? 'Anchor' : (orgUsers.find(ou => ou.id === v)?.name || prev.refunded_by) }));
                    }}>
                      <SelectTrigger className="h-9 text-sm" data-testid="refund-from-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="billportal">BillPortal</SelectItem>
                        <SelectItem value="anchor">Anchor (3rd Party)</SelectItem>
                        {orgUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">To (Org / User)</label>
                    <Input className="h-9 text-sm" value={refundForm.refund_to || ''} onChange={e => setRefundForm(prev => ({ ...prev, refund_to: e.target.value }))} placeholder={orgName || 'Org name'} data-testid="refund-to-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Reference</label>
                    <Input className="h-9 text-sm font-mono" value={refundForm.refund_reference || ''} onChange={e => setRefundForm(prev => ({ ...prev, refund_reference: e.target.value }))} placeholder="Ref #" data-testid="refund-ref-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Refund Date</label>
                    <Input
                      type="date"
                      className="h-9 text-sm"
                      value={refundForm.refund_date ? refundForm.refund_date.split('T')[0] : ''}
                      onChange={e => setRefundForm(prev => ({ ...prev, refund_date: e.target.value }))}
                      disabled={!!refundForm.refund_completed}
                      data-testid="refund-date-input"
                    />
                    {refundForm.refund_completed && (
                      <p className="text-[10px] text-muted-foreground mt-1">Locked: refund completion date</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground uppercase mb-1.5 block">Method / Notes</label>
                  <textarea className="w-full min-h-[70px] p-2 text-sm border border-input bg-background rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-ring" value={refundForm.refund_method} onChange={e => setRefundForm(prev => ({ ...prev, refund_method: e.target.value }))} placeholder="Credit note, ACH reversal..." data-testid="refund-method-input" />
                </div>
                {/* REFUNDED CHECKBOX — toggles To Refund vs Refunded */}
                <div className="flex items-start gap-3 p-3 rounded-md border border-emerald-200 bg-emerald-50/40">
                  <input
                    type="checkbox"
                    id="refund-completed-checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                    checked={!!refundForm.refund_completed}
                    onChange={e => setRefundForm(prev => ({
                      ...prev,
                      refund_completed: e.target.checked,
                      // Auto-stamp today's date when marking as refunded (date locks via disabled prop above)
                      refund_date: e.target.checked
                        ? new Date().toISOString().split('T')[0]
                        : prev.refund_date,
                    }))}
                    data-testid="refund-completed-checkbox"
                  />
                  <label htmlFor="refund-completed-checkbox" className="cursor-pointer flex-1">
                    <p className="text-sm font-semibold text-emerald-900">Refunded</p>
                    <p className="text-[11px] text-emerald-700 mt-0.5">
                      Tick this once the refund has been actually processed/sent. Leave unchecked while it&apos;s still pending (status: <span className="font-semibold">To Refund</span>).
                    </p>
                  </label>
                </div>
              </div>
            )}
          </div>
          <div className="border-t border-border px-6 py-3 flex items-center gap-2 justify-between">
            <div>
              {refundForm._locked && (
                <Button variant="outline" size="sm" className="text-xs" onClick={() => setRefundForm(prev => ({ ...prev, _locked: false }))} data-testid="refund-edit-btn">
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
              )}
              {!refundForm._locked && refundForm.isEdit && (
                <Button variant="outline" size="sm" className="text-xs text-red-600 border-red-200" onClick={clearRefund} disabled={refundSaving} data-testid="clear-refund-btn">
                  <Trash2 className="h-3 w-3 mr-1" />Clear
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setRefundDrawerOpen(false)}>Close</Button>
              {!refundForm._locked && (
                <Button size="sm" className="text-xs bg-red-600 hover:bg-red-700 text-white" onClick={saveRefund} disabled={refundSaving} data-testid="save-refund-btn">
                  {refundSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ArrowRightLeft className="h-3 w-3 mr-1" />}
                  {refundForm.isEdit ? 'Update' : 'Record'} Refund
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Table */}
      <div className="flex-1 overflow-auto" ref={scrollContainerRef} data-testid="pf-utilities-table-container">
        {displayUtils.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground"><FileText className="h-12 w-12 mb-3 opacity-40" /><p className="text-sm">{showIssuesOnly ? 'No issues found' : 'No invoice lines found'}</p></div>
        ) : (
          <div className={(isPaid || isApproved) ? 'min-w-[5400px]' : 'min-w-[4200px]'}>
            <table className="w-full text-xs border-collapse" data-testid="pf-utilities-table">
              <thead className="sticky top-0 z-20">
                <tr className="bg-card border-b-2 border-border">
                  <th className="h-8 px-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground border-b whitespace-nowrap text-center sticky left-0 z-30 bg-card" style={{ width: '40px', minWidth: '40px' }}>#</th>
                  <th className={`h-8 px-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground border-b whitespace-nowrap text-center sticky left-[40px] z-30 bg-card ${!canEdit ? 'shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]' : ''}`} style={{ width: '120px', minWidth: '120px' }}>Flags</th>
                  {canEdit && <th className="h-8 px-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground border-b whitespace-nowrap text-center sticky left-[160px] z-30 bg-card shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]" style={{ width: '65px', minWidth: '65px' }}>Actions</th>}
                  <Th w="260">Bill ID</Th>
                  <Th w="150">Property Name</Th>
                  <Th w="130">Supplier</Th>
                  <Th w="100">Account No</Th>
                  <Th w="170">Service Address</Th>
                  <Th w="100">BP Identifier</Th>
                  <Th w="100">Invoice No</Th>
                  <Th w="80">Issue Date</Th>
                  <Th w="80">Period From</Th>
                  <Th w="80">Period To</Th>
                  <Th w="80">Due Date</Th>
                  <Th w="100" right editable={canEdit}>Usage Charges</Th>
                  <Th w="100" right editable={canEdit}>BBF</Th>
                  <Th w="100" right editable={canEdit}>New Charges</Th>
                  <Th w="90" right editable={canEdit}>Credits</Th>
                  <Th w="80" right editable={canEdit}>Late Fee</Th>
                  <Th w="100" right editable={canEdit}>Other Charges</Th>
                  <Th w="110" right editable={canEdit}>Total Amt Due</Th>
                  <Th w="120" right editable={canEdit}>{(isApproved || isPaid) ? 'Approved Amt' : 'Total Requested'}</Th>
                  <Th w="30"></Th>
                  <Th w="120" editable={canEdit}>Payment Sel.</Th>
                  <Th w="80">Commodity</Th>
                  <Th w="160">Site Legal Entity</Th>
                  <Th w="100">EIN</Th>
                  <Th w="80" editable={canEdit}>GL Code</Th>
                  <Th w="80">Common/Unit</Th>
                  <Th w="90">Pmt Req. Date</Th>
                  {(isPaid || isApproved) && <Th w="100" right>Paid Amount</Th>}
                  {(isPaid || isApproved) && <Th w="100" right>Variance</Th>}
                  {(isPaid || isApproved) && <Th w="90" right>Svc Fee</Th>}
                  {(isPaid || isApproved) && <Th w="120">Txn Reference</Th>}
                  {(isPaid || isApproved) && <Th w="90">Txn Date</Th>}
                  {(isPaid || isApproved) && <Th w="70" className="text-center">Refund</Th>}
                  {(isPaid || isApproved) && <Th w="100" right>Refund Amt</Th>}
                  {(isPaid || isApproved) && <Th w="130">Refunded By</Th>}
                  {(isPaid || isApproved) && <Th w="150">Refund Method</Th>}
                </tr>
              </thead>
              <tbody>
                {displayUtils.map((u, idx) => {
                  const bill = billsMap[u.bill_id] || {};
                  const sortedIdx = sortedUtils.indexOf(u);
                  const isPrimary = billFirstIdx[u.bill_id] === sortedIdx;
                  const isEditing = editRowIdx === idx;
                  const utilFlags = issueFlagsByUtil[u.payment_file_utility_id] || [];
                  const utilDups = dupInfo[u.payment_file_utility_id] || [];
                  const isDuplicate = utilDups.length > 0;
                  const isDupCopy = utilDups.some(d => d.type === 'exact_intra_pf' && !d.is_original);
                  const isCrossPfDup = crossPfBillIds.has(u.bill_id);
                  const hasIssue = isDuplicate || utilFlags.length > 0;
                  const isMatching = isPrimary && !hasIssue && !isCrossPfDup;
                  const isSelected = selectedIssues.has(u.payment_file_utility_id);

                  const isHighlighted = highlightedRowId === u.payment_file_utility_id;

                  // Row backgrounds — cross-PF duplicates get darkest alert
                  const rowBg = isHighlighted ? 'bg-blue-100 ring-2 ring-inset ring-blue-400'
                    : isEditing ? 'bg-blue-50 ring-1 ring-inset ring-blue-300'
                    : isCrossPfDup ? 'bg-red-100 ring-1 ring-inset ring-red-400'
                    : isDupCopy ? 'bg-red-50/60'
                    : utilFlags.length > 0 ? 'bg-orange-50'
                    : isPrimary ? 'bg-card' : 'bg-gray-100';
                  const stickyBg = isHighlighted ? 'bg-blue-100'
                    : isEditing ? 'bg-blue-50'
                    : isCrossPfDup ? 'bg-red-100'
                    : isDupCopy ? 'bg-red-50'
                    : utilFlags.length > 0 ? 'bg-orange-50'
                    : isPrimary ? 'bg-card' : 'bg-gray-100';

                  return (
                    <tr
                      key={u.payment_file_utility_id || idx}
                      className={`border-b border-border/50 hover:bg-muted/20 transition-colors cursor-pointer ${rowBg}`}
                      data-testid={`util-row-${idx}`}
                      onClick={(e) => {
                        // Don't highlight if clicking on buttons, inputs, or interactive elements
                        if (e.target.closest('button, input, select, a, [role="button"]')) return;
                        setHighlightedRowId(prev => prev === u.payment_file_utility_id ? null : u.payment_file_utility_id);
                      }}
                    >
                      {/* Sticky # column with checkbox for issues */}
                      <td className={`px-1 py-1 text-xs text-center text-muted-foreground whitespace-nowrap sticky left-0 z-[5] ${stickyBg}`} style={{ width: '40px', minWidth: '40px' }}>
                        {hasIssue && canEdit ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleIssueSelection(u.payment_file_utility_id)}
                            className="h-3 w-3 accent-red-600 cursor-pointer"
                            data-testid={`issue-checkbox-${idx}`}
                          />
                        ) : (
                          <span>{idx + 1}</span>
                        )}
                      </td>

                      {/* Sticky Flags column with issue labels + dismiss */}
                      <td className={`px-1 py-1 text-xs text-center sticky left-[40px] z-[5] ${!canEdit ? 'shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]' : ''} ${stickyBg}`} style={{ width: '120px', minWidth: '120px' }}>
                        <div className="flex items-center gap-0.5 justify-center flex-wrap">
                          {isCrossPfDup && (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="inline-flex items-center justify-center h-4 w-4 rounded bg-red-600 text-white text-[9px] font-bold animate-pulse" data-testid={`cross-pf-badge-${idx}`}>!</span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-xs text-xs">
                                <p className="font-bold text-red-600">CRITICAL: Same Bill ID exists in another Payment File</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                          {isDuplicate && <DupBadge dups={utilDups} accountId={accountId} />}
                          {utilFlags.includes('mismatch') && (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="inline-flex items-center px-1 h-4 rounded text-[8px] font-semibold bg-orange-100 text-orange-700 border border-orange-300" data-testid={`flag-mismatch-${idx}`}>MISMATCH</span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="text-xs">Payment selection vs calculated amount mismatch</TooltipContent>
                            </Tooltip>
                          )}
                          {utilFlags.includes('blank_gl') && (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="inline-flex items-center px-1 h-4 rounded text-[8px] font-semibold bg-pink-100 text-pink-700 border border-pink-300" data-testid={`flag-blank-gl-${idx}`}>NO GL</span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="text-xs">GL code is blank</TooltipContent>
                            </Tooltip>
                          )}
                          {utilFlags.includes('blank_address') && (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="inline-flex items-center px-1 h-4 rounded text-[8px] font-semibold bg-yellow-100 text-yellow-700 border border-yellow-300" data-testid={`flag-blank-addr-${idx}`}>NO ADDR</span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="text-xs">Service address is blank</TooltipContent>
                            </Tooltip>
                          )}
                          {utilFlags.includes('address_mismatch') && (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="inline-flex items-center px-1 h-4 rounded text-[8px] font-semibold bg-cyan-100 text-cyan-700 border border-cyan-300" data-testid={`flag-addr-mismatch-${idx}`}>ADDR</span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="text-xs">Service address doesn't match UBM record</TooltipContent>
                            </Tooltip>
                          )}
                          {utilFlags.includes('child_duplicate') && (
                            <Tooltip>
                              <TooltipTrigger>
                                <span className="inline-flex items-center px-1 h-4 rounded text-[8px] font-semibold bg-violet-100 text-violet-700 border border-violet-300" data-testid={`flag-child-dup-${idx}`}>CHILD DUP</span>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="text-xs">Duplicate child row under same bill</TooltipContent>
                            </Tooltip>
                          )}
                          {isMatching && <Check className="h-3 w-3 text-emerald-500" data-testid={`check-match-${idx}`} />}
                          {hasIssue && canEdit && (
                            <Tooltip>
                              <TooltipTrigger>
                                <button
                                  onClick={() => dismissIssue(u.payment_file_utility_id, u.bill_id, 'single')}
                                  disabled={dismissingId === u.payment_file_utility_id}
                                  className="text-muted-foreground hover:text-violet-600 p-0.5 rounded hover:bg-violet-50 transition-colors"
                                  data-testid={`dismiss-issue-${idx}`}
                                >
                                  {dismissingId === u.payment_file_utility_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldOff className="h-3 w-3" />}
                                </button>
                              </TooltipTrigger>
                              <TooltipContent side="right" className="text-xs">Dismiss issue</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </td>

                      {/* Actions column - sticky */}
                      {canEdit && (
                        <td className={`px-2 py-1 text-xs text-center whitespace-nowrap sticky left-[160px] z-[5] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] ${stickyBg}`} style={{ width: '65px', minWidth: '65px' }}>
                          <div className="flex items-center gap-0.5 justify-center">
                            {isEditing ? (
                              <>
                                <button onClick={saveRowEdit} disabled={saving} className="text-emerald-600 hover:text-emerald-700 p-0.5 rounded hover:bg-emerald-50" title="Save" data-testid={`save-row-${idx}`}>
                                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                </button>
                                <button onClick={cancelRowEdit} className="text-red-500 hover:text-red-600 p-0.5 rounded hover:bg-red-50" title="Cancel" data-testid={`cancel-row-${idx}`}><XCircle className="h-3.5 w-3.5" /></button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => startRowEdit(idx)} className="text-muted-foreground hover:text-blue-600 p-0.5 rounded hover:bg-blue-50 transition-colors" title="Edit row" data-testid={`edit-row-btn-${idx}`}><Pencil className="h-3.5 w-3.5" /></button>
                                <button onClick={() => setRemoveConfirm({ idx, utilId: u.payment_file_utility_id })} className="text-muted-foreground hover:text-red-600 p-0.5 rounded hover:bg-red-50 transition-colors" title="Remove row" data-testid={`remove-row-btn-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
                              </>
                            )}
                          </div>
                        </td>
                      )}

                      <Td mono title={u.bill_id}>{u.bill_id || '\u2014'}</Td>
                      <Td truncate title={u.site_name}>{u.site_name || '\u2014'}</Td>
                      <Td truncate title={u.supplier_name}>{u.supplier_name || '\u2014'}</Td>
                      <Td mono>{u.account_number || '\u2014'}</Td>
                      <Td truncate title={u.service_address || u.account_address}>{u.service_address || u.account_address || '\u2014'}</Td>
                      <Td mono>{u.sub_account_number || '\u2014'}</Td>
                      <Td mono>{u.invoice_number || '\u2014'}</Td>
                      <Td>{fmtD(u.invoice_date)}</Td>
                      <Td>{fmtD(u.period_from)}</Td>
                      <Td>{fmtD(u.period_to)}</Td>
                      <Td>{fmtD(u.due_date)}</Td>

                      <FieldCell value={u.service_total} field="service_total" isEditing={isEditing} editValues={editValues} setEditValues={setEditValues} />
                      <FieldCell value={bill.opening_balance} field="opening_balance" isEditing={isEditing && isPrimary} editValues={editValues} setEditValues={setEditValues} />
                      <FieldCell value={bill.current_bill_amount} field="current_bill_amount" isEditing={isEditing && isPrimary} editValues={editValues} setEditValues={setEditValues} />
                      <FieldCell value={bill.discount_amount} field="discount_amount" isEditing={isEditing && isPrimary} editValues={editValues} setEditValues={setEditValues} />
                      <FieldCell value={bill.late_payment_fee} field="late_payment_fee" isEditing={isEditing && isPrimary} editValues={editValues} setEditValues={setEditValues} />
                      <FieldCell value={bill.adjustments} field="adjustments" isEditing={isEditing && isPrimary} editValues={editValues} setEditValues={setEditValues} />
                      <FieldCell value={bill.total_amount_due} field="total_amount_due" isEditing={isEditing && isPrimary} editValues={editValues} setEditValues={setEditValues} bold={isPrimary} />
                      <FieldCell value={bill.payment_amount} field="payment_amount" isEditing={isEditing && isPrimary} editValues={editValues} setEditValues={setEditValues} bold={isPrimary} highlight={isPrimary} />

                      <Td className="text-center">
                        {(isApproved || isPaid) && isPrimary ? (
                          (txnMap[u.payment_file_utility_id] || u.payment_status === 'paid' || bill.payment_status === 'paid') ? (
                            <button
                              onClick={() => openTxnDrawer(u)}
                              className="inline-flex items-center gap-0.5 text-emerald-600 hover:text-emerald-700 font-medium"
                              title="View payment details"
                              data-testid={`txn-edit-${idx}`}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              <span className="text-[9px] font-semibold">Paid</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => openTxnDrawer(u)}
                              className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-700"
                              title="Record payment"
                              data-testid={`txn-pay-${idx}`}
                            >
                              <CreditCard className="h-3.5 w-3.5" />
                            </button>
                          )
                        ) : (
                          <button onClick={() => openPdf(u.bill_id)} className="text-muted-foreground hover:text-blue-600 transition-colors" title="View Bill PDF" data-testid={`pdf-icon-${idx}`}><FileText className="h-3.5 w-3.5" /></button>
                        )}
                      </Td>

                      {isEditing && isPrimary && 'payment_selection' in editValues ? (
                        <td className="px-1 py-0.5">
                          <Select value={editValues.payment_selection || ''} onValueChange={v => setEditValues(prev => ({ ...prev, payment_selection: v }))}>
                            <SelectTrigger className="h-6 w-28 text-xs" data-testid="edit-input-payment_selection"><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              {PAYMENT_SEL_OPTIONS.map(opt => <SelectItem key={opt} value={opt} className="text-xs">{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                      ) : (
                        <td className="px-2 py-1 text-xs whitespace-nowrap">
                          {isPrimary && bill.payment_selection ? (
                            <PaymentSelBadge
                              bill={bill}
                              utility={u}
                              pfId={pfId}
                              accountId={accountId}
                              pfStatus={pf?.status}
                              idx={idx}
                              onViewPdf={openPdf}
                            />
                          ) : (
                            <span className="text-muted-foreground">{'\u2014'}</span>
                          )}
                        </td>
                      )}

                      <Td>{u.utility_type || '\u2014'}</Td>
                      <Td truncate className="text-[10px]" title={u.legal_entity_name}>{u.legal_entity_name || '\u2014'}</Td>
                      <Td mono>{u.ein || '\u2014'}</Td>
                      <FieldCell value={u.gl_code} field="gl_code" isEditing={isEditing} editValues={editValues} setEditValues={setEditValues} isText />
                      <Td>{u.sub_account_number ? 'Unit' : 'Common'}</Td>
                      <Td>{fmtD(u.payment_file_date)}</Td>
                      {(isPaid || isApproved) && <Td right mono className={u.paid_amount ? 'text-emerald-700 font-medium' : 'text-muted-foreground'}>{u.paid_amount ? fmtC(u.paid_amount) : '\u2014'}</Td>}
                      {(isPaid || isApproved) && (() => {
                        const requested = isPrimary ? (parseFloat(bill.payment_amount) || 0) : 0;
                        const paid = parseFloat(u.paid_amount) || 0;
                        const hasVariance = isPrimary && paid > 0;
                        const variance = hasVariance ? paid - requested : null;
                        const absVar = variance != null ? Math.abs(variance) : 0;
                        return (
                          <Td right mono className={absVar >= 0.005 ? (variance > 0 ? 'text-red-600 font-medium' : 'text-amber-600 font-medium') : 'text-muted-foreground'} data-testid={`variance-cell-${idx}`}>
                            {hasVariance && absVar >= 0.005 ? (variance > 0 ? '+' : '') + fmtC(variance) : hasVariance ? '$0.00' : '\u2014'}
                          </Td>
                        );
                      })()}
                      {(isPaid || isApproved) && <Td right mono className={parseFloat(u.payment_service_charge) > 0 ? 'text-red-600 font-medium' : 'text-muted-foreground'}>{parseFloat(u.payment_service_charge) > 0 ? fmtC(u.payment_service_charge) : '\u2014'}</Td>}
                      {(isPaid || isApproved) && <Td mono>{u.transaction_reference || '\u2014'}</Td>}
                      {(isPaid || isApproved) && <Td>{u.transaction_date ? fmtD(u.transaction_date) : '\u2014'}</Td>}
                      {(isPaid || isApproved) && (() => {
                        const txn = txnMap[u.payment_file_utility_id];
                        const hasRefund = txn?.refund === 'yes' || u.refund === 'yes';
                        return (
                          <td className="px-2 py-1 text-xs text-center whitespace-nowrap">
                            {isPrimary ? (
                              <button
                                onClick={() => openRefundDrawer(u)}
                                className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border cursor-pointer transition-colors ${
                                  hasRefund
                                    ? 'bg-red-500/10 text-red-600 border-red-500/20 hover:bg-red-500/20'
                                    : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
                                }`}
                                data-testid={`refund-btn-${idx}`}
                              >
                                {hasRefund ? 'Yes' : 'No'}
                              </button>
                            ) : <span className="text-muted-foreground">{'\u2014'}</span>}
                          </td>
                        );
                      })()}
                      {(isPaid || isApproved) && (() => {
                        const txn = txnMap[u.payment_file_utility_id];
                        const refundAmt = txn?.refund_amount || u.refund_amount;
                        return <Td right mono className={refundAmt ? 'text-red-600 font-medium' : 'text-muted-foreground'}>{refundAmt ? fmtC(refundAmt) : '\u2014'}</Td>;
                      })()}
                      {(isPaid || isApproved) && (() => {
                        const txn = txnMap[u.payment_file_utility_id];
                        return <Td truncate title={txn?.refunded_by || u.refunded_by}>{txn?.refunded_by || u.refunded_by || '\u2014'}</Td>;
                      })()}
                      {(isPaid || isApproved) && (() => {
                        const txn = txnMap[u.payment_file_utility_id];
                        return <Td truncate title={txn?.refund_method || u.refund_method}>{txn?.refund_method || u.refund_method || '\u2014'}</Td>;
                      })()}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div ref={sentinelRef} className="h-4" />
          </div>
        )}
      </div>

      {/* Sticky Load More Bar */}
      {hasMore && (
        <div className="border-t border-border bg-card px-4 py-2 flex-shrink-0 flex items-center justify-center gap-3" data-testid="load-more-bar">
          {loadingMore ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Button variant="outline" size="sm" onClick={loadMore} data-testid="load-more-btn" className="text-xs">
              Load Next 100 ({allUtils.length} of {utilTotal} loaded)
            </Button>
          )}
        </div>
      )}
      {!hasMore && allUtils.length > 0 && allUtils.length >= utilLimit && (
        <div className="border-t border-border bg-muted/20 px-4 py-1.5 flex-shrink-0 text-center">
          <span className="text-xs text-muted-foreground">All {utilTotal} rows loaded</span>
        </div>
      )}

      {/* PDF Panel */}
      {pdfOpen && (
        <div className="fixed inset-y-0 left-0 w-1/2 bg-card border-r-2 border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-left-full duration-300" data-testid="pdf-viewer-panel">
          <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
            <h3 className="text-sm font-semibold">Bill PDF Viewer</h3>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPdfZoom(z => Math.max(0.5, z - 0.25))} data-testid="pdf-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
              <span className="text-xs font-mono w-12 text-center">{Math.round(pdfZoom * 100)}%</span>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPdfZoom(z => Math.min(3, z + 0.25))} data-testid="pdf-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 ml-2" onClick={() => { setPdfOpen(false); setPdfZoom(1); }} data-testid="pdf-close"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="flex-1 overflow-auto bg-muted/10">
            {pdfLoading ? <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
              : (pdfDataUrl || pdfUrl) ? <div style={{ transform: `scale(${pdfZoom})`, transformOrigin: 'top left' }}><iframe src={pdfDataUrl || pdfUrl} title="Bill PDF" className="w-full border-0" style={{ height: `${100 / pdfZoom}vh` }} /></div>
              : <div className="flex flex-col items-center justify-center h-full text-muted-foreground"><FileText className="h-12 w-12 mb-3 opacity-40" /><p className="text-sm font-medium">{pdfMessage || 'No PDF available'}</p></div>
            }
          </div>
        </div>
      )}

      {/* Audit Drawer */}
      <Sheet open={auditOpen} onOpenChange={setAuditOpen}>
        <SheetContent side="right" className="w-1/2 max-w-none p-0 flex flex-col" data-testid="audit-log-drawer">
          <SheetHeader className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <SheetTitle className="text-sm">Audit Log ({auditLog.length} entries)</SheetTitle>
              <div className="flex items-center gap-2">
                {hasRowEdits && canEdit && (
                  <Button variant="outline" size="sm" className="h-7 text-xs text-orange-600 border-orange-300 hover:bg-orange-50" onClick={revertAll} disabled={revertingAll} data-testid="revert-all-btn">
                    {revertingAll ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />} Revert All
                  </Button>
                )}
                {auditLog.length > 0 && <Button variant="outline" size="sm" className="h-7 text-xs" onClick={downloadAuditCSV} data-testid="audit-download-btn"><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>}
              </div>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-auto">
            {auditLog.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-16"><History className="h-10 w-10 mb-2 opacity-40" /><p className="text-sm">No edit history yet</p></div>
            ) : (
              <Table data-testid="audit-log-table">
                <TableHeader><TableRow className="bg-muted/30">
                  <TableHead className="text-[10px] uppercase font-semibold w-12">Row</TableHead>
                  <TableHead className="text-[10px] uppercase font-semibold w-40">Timestamp</TableHead>
                  <TableHead className="text-[10px] uppercase font-semibold w-24">User</TableHead>
                  <TableHead className="text-[10px] uppercase font-semibold w-20">Action</TableHead>
                  <TableHead className="text-[10px] uppercase font-semibold w-28">Column</TableHead>
                  <TableHead className="text-[10px] uppercase font-semibold w-24">Old Value</TableHead>
                  <TableHead className="text-[10px] uppercase font-semibold w-24">New Value</TableHead>
                  {canEdit && <TableHead className="text-[10px] uppercase font-semibold w-16">Revert</TableHead>}
                </TableRow></TableHeader>
                <TableBody>
                  {auditLog.map((log, i) => {
                    const isRowEdit = log.action === 'row_edit';
                    const isRevertAction = log.action === 'revert_edit' || log.action === 'revert_all';
                    const isRemoval = log.action === 'row_removed';
                    const isApproveAction = log.action === 'approve_payment_file';
                    const isMarkPaid = log.action === 'mark_paid';
                    const isFundsPulled = log.action === 'funds_pulled';
                    const isFundsRemoved = log.action === 'funds_pulled_removed';
                    const isEditAmount = log.action === 'edit_amount';
                    const isBulkDismiss = log.action === 'bulk_issues_dismissed';
                    const isSystemAction = isApproveAction || isMarkPaid || isFundsPulled || isFundsRemoved || isEditAmount || isBulkDismiss;

                    // Determine action label and color
                    let actionLabel = log.action;
                    let actionColor = 'bg-blue-50 text-blue-700 border-blue-200';
                    let rowBg = '';
                    if (isRowEdit) { actionLabel = 'Edit'; }
                    else if (log.action === 'revert_edit') { actionLabel = 'Revert'; actionColor = 'bg-orange-100 text-orange-700 border-orange-200'; rowBg = 'bg-orange-50/50'; }
                    else if (log.action === 'revert_all') { actionLabel = 'Revert All'; actionColor = 'bg-orange-100 text-orange-700 border-orange-200'; rowBg = 'bg-orange-50/50'; }
                    else if (isRemoval) { actionLabel = 'Removed'; actionColor = 'bg-red-100 text-red-700 border-red-200'; rowBg = 'bg-red-50/50'; }
                    else if (isApproveAction) { actionLabel = 'Approved'; actionColor = 'bg-blue-100 text-blue-700 border-blue-200'; rowBg = 'bg-blue-50/30'; }
                    else if (isMarkPaid) { actionLabel = 'Paid'; actionColor = 'bg-emerald-100 text-emerald-700 border-emerald-200'; rowBg = 'bg-emerald-50/30'; }
                    else if (isFundsPulled) { actionLabel = 'Fund Pull'; actionColor = 'bg-violet-100 text-violet-700 border-violet-200'; rowBg = 'bg-violet-50/30'; }
                    else if (isFundsRemoved) { actionLabel = 'Fund Removed'; actionColor = 'bg-red-100 text-red-700 border-red-200'; rowBg = 'bg-red-50/30'; }
                    else if (isEditAmount) { actionLabel = 'Edit Amount'; actionColor = 'bg-amber-100 text-amber-700 border-amber-200'; }
                    else if (isBulkDismiss) { actionLabel = 'Issues Resolved'; actionColor = 'bg-violet-100 text-violet-700 border-violet-200'; }

                    // Determine column/detail display
                    let columnDisplay = '\u2014';
                    let oldValue = log.details?.old_value ?? '\u2014';
                    let newValue = log.details?.new_value ?? '\u2014';

                    if (isRowEdit) {
                      columnDisplay = FIELD_DISPLAY[log.details?.field_name] || log.details?.field_name || '\u2014';
                    } else if (isRemoval) {
                      columnDisplay = `${log.details?.supplier_name || ''} - $${log.details?.pay_amount || ''}`;
                      oldValue = '\u2014'; newValue = '\u2014';
                    } else if (isRevertAction && log.action === 'revert_all') {
                      columnDisplay = `${log.details?.total_entries_reverted || 0} fields`;
                    } else if (isRevertAction && log.action === 'revert_edit') {
                      columnDisplay = FIELD_DISPLAY[log.details?.field_name] || log.details?.field_name || '\u2014';
                    } else if (isApproveAction) {
                      columnDisplay = 'Status';
                      oldValue = log.details?.old_status || 'review';
                      newValue = log.details?.new_status || 'approved';
                    } else if (isMarkPaid) {
                      columnDisplay = 'Status';
                      oldValue = log.details?.old_status || 'approved';
                      newValue = log.details?.new_status || 'paid';
                    } else if (isFundsPulled) {
                      columnDisplay = log.details?.notes || 'Funds Pulled';
                      oldValue = '\u2014';
                      newValue = `$${Number(log.details?.amount || 0).toFixed(2)}`;
                    } else if (isFundsRemoved) {
                      columnDisplay = log.details?.notes || 'Fund Removed';
                      oldValue = `$${Number(log.details?.removed_amount || 0).toFixed(2)}`;
                      newValue = '\u2014';
                    } else if (isEditAmount) {
                      columnDisplay = 'Total Requested';
                      oldValue = `$${Number(log.details?.old_value?.total_requested_amount || 0).toFixed(2)}`;
                      newValue = `$${Number(log.details?.new_value?.total_requested_amount || 0).toFixed(2)}`;
                    } else if (isBulkDismiss) {
                      columnDisplay = `${log.details?.count || 0} issues`;
                      oldValue = '\u2014'; newValue = 'Resolved';
                    }

                    return (
                      <TableRow key={log.id || i} className={`text-xs ${rowBg}`}>
                        <TableCell className="font-mono">{log.details?.row_number || (isSystemAction ? '\u2014' : '\u2014')}</TableCell>
                        <TableCell className="text-muted-foreground">{fmtDT(log.created_at)}</TableCell>
                        <TableCell>{log.user_name || '\u2014'}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[9px] ${actionColor}`}>
                            {actionLabel}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{columnDisplay}</TableCell>
                        <TableCell className="font-mono text-red-600">{typeof oldValue === 'object' ? JSON.stringify(oldValue) : oldValue}</TableCell>
                        <TableCell className="font-mono text-emerald-600">{typeof newValue === 'object' ? JSON.stringify(newValue) : newValue}</TableCell>
                        {canEdit && (
                          <TableCell>
                            {isRowEdit && (
                              <button onClick={() => revertEntry(log.id)} disabled={revertingId === log.id} className="text-orange-500 hover:text-orange-700 p-0.5 rounded hover:bg-orange-50" title="Revert" data-testid={`revert-entry-${i}`}>
                                {revertingId === log.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                              </button>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Remove Row Confirm */}
      <Dialog open={!!removeConfirm} onOpenChange={v => !v && setRemoveConfirm(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="remove-row-dialog">
          <DialogHeader><DialogTitle>Remove Row</DialogTitle><DialogDescription>This will remove the invoice line from the payment file.</DialogDescription></DialogHeader>
          {removeConfirm && (() => {
            const u = displayUtils.find(x => x.payment_file_utility_id === removeConfirm.utilId);
            return u ? <p className="text-sm text-muted-foreground py-2">Remove <strong>{u.supplier_name || u.account_number}</strong> row ({fmtC(u.pay_amount)})?</p> : null;
          })()}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setRemoveConfirm(null)}>Cancel</Button>
            <Button size="sm" variant="destructive" onClick={removeRow} disabled={!!removingId} data-testid="remove-row-confirm">
              {removingId ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />} Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve / Mark Paid */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="approve-dialog">
          <DialogHeader><DialogTitle>Approve Payment File</DialogTitle><DialogDescription>Review and confirm.</DialogDescription></DialogHeader>
          {((issueSummary?.total_mismatches ?? 0) > 0 || totalDupes > 0) && (
            <div className="bg-orange-50 border border-orange-200 rounded-md p-3 text-xs text-orange-800 space-y-1">
              {(issueSummary?.total_mismatches ?? 0) > 0 && <p><AlertTriangle className="h-3.5 w-3.5 inline mr-1" /><strong>{issueSummary.total_mismatches} validation mismatch{issueSummary.total_mismatches > 1 ? 'es' : ''}</strong></p>}
              {totalDupes > 0 && <p><Copy className="h-3.5 w-3.5 inline mr-1" /><strong>{totalDupes} duplicate{totalDupes > 1 ? 's' : ''}</strong> detected</p>}
            </div>
          )}
          <p className="text-sm text-muted-foreground py-2">Approve <strong>{pf.batch_name}</strong> with <strong>{pf.total_bills} bills</strong> totaling <strong>{fmtC(pf.total_requested_amount)}</strong>?</p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleApprove} disabled={actionLoading} data-testid="approve-confirm-btn">
              {actionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />} Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={markPaidOpen} onOpenChange={setMarkPaidOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="mark-paid-dialog">
          <DialogHeader><DialogTitle>Mark as Paid</DialogTitle><DialogDescription>Confirm payment.</DialogDescription></DialogHeader>
          <p className="text-sm text-muted-foreground py-2">Mark <strong>{pf.batch_name}</strong> as Paid?</p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setMarkPaidOpen(false)}>Cancel</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleMarkPaid} disabled={actionLoading} data-testid="mark-paid-confirm-btn">
              {actionLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Banknote className="h-3.5 w-3.5 mr-1" />} Mark as Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TooltipProvider>
  );
}

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

function Stat({ label, value, highlight }) {
  return (<div><span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span><p className={`text-base font-bold ${highlight ? 'text-blue-600' : ''}`}>{value}</p></div>);
}

function Th({ children, w, right, editable, className = '' }) {
  return (
    <th className={`h-8 px-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground border-b whitespace-nowrap ${right ? 'text-right' : 'text-left'} ${className}`} style={{ width: w ? `${w}px` : undefined, minWidth: w ? `${w}px` : undefined }}>
      <span className="flex items-center gap-1">{right && <span className="flex-1" />}{children}{editable && <span className="text-blue-500 text-[8px]">*</span>}</span>
    </th>
  );
}

function Td({ children, right, mono, truncate: trunc, className = '', title }) {
  return (<td className={`px-2 py-1 text-xs whitespace-nowrap ${right ? 'text-right' : ''} ${mono ? 'font-mono text-[11px]' : ''} ${trunc ? 'truncate max-w-[170px] overflow-hidden' : ''} ${className}`} title={title}>{children}</td>);
}

function FieldCell({ value, field, isEditing, editValues, setEditValues, bold, highlight, isText }) {
  const displayVal = isText ? (value || '\u2014') : fmtC(value);
  if (isEditing && field in editValues) {
    return (
      <td className="px-1 py-0.5 text-right">
        <Input type={isText ? 'text' : 'number'} step="0.01" value={editValues[field] ?? ''} onChange={e => setEditValues(prev => ({ ...prev, [field]: e.target.value }))} className="h-6 w-24 text-xs p-1 font-mono bg-white" data-testid={`edit-input-${field}`} />
      </td>
    );
  }
  return (
    <td className={`px-2 py-1 text-xs text-right whitespace-nowrap font-mono text-[11px] ${bold ? 'font-semibold' : ''} ${highlight ? 'text-blue-700 font-bold' : ''}`} data-testid={`cell-${field}`}>{displayVal}</td>
  );
}

/** Payment Selection badge with click-to-expand comparison popover */
function PaymentSelBadge({ bill, utility, pfId, accountId, pfStatus, idx, onViewPdf }) {
  const [open, setOpen] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);

  const sel = bill.payment_selection || '';
  const isNC = sel === 'New Charges';
  const badgeColor = isNC
    ? 'bg-sky-500/10 text-sky-700 border-sky-500/20 hover:bg-sky-500/20'
    : 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20 hover:bg-emerald-500/20';

  const loadHistory = async () => {
    if (historyData) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/payments/v2/files/${pfId}/bill-history/${bill.bill_id}`, {
        params: { account_id: accountId },
      });
      setHistoryData(data);
    } catch {
      setHistoryData({ error: true });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) loadHistory();
  };

  const seqLabel = historyData?.sequence_label || '';
  const current = historyData?.current;
  const previous = historyData?.previous;
  const paymentInfo = historyData?.payment_info;
  const isApprovedOrPaid = pfStatus === 'approved' || pfStatus === 'paid';

  const comparisonRows = [
    { label: 'Account No', key: 'account_number', fmt: v => v || '\u2014' },
    { label: 'Issue Date', key: 'invoice_date', fmt: v => fmtD(v) },
    { label: 'New Charges', key: 'new_charges', fmt: v => fmtC(v) },
    { label: 'Total Amt Due', key: 'total_amount_due', fmt: v => fmtC(v) },
    { label: 'BBF', key: 'bbf', fmt: v => fmtC(v) },
    { label: 'Credits', key: 'credits', fmt: v => fmtC(v) },
    { label: 'Due Date', key: 'due_date', fmt: v => fmtD(v) },
    { label: 'Pmt Status', key: 'payment_status', fmt: v => v ? v.charAt(0).toUpperCase() + v.slice(1) : '\u2014' },
  ];

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border cursor-pointer transition-colors ${badgeColor}`}
          data-testid={`payment-sel-badge-${idx}`}
        >
          {isNC ? 'NC' : 'TAD'}
          <Info className="h-2.5 w-2.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="left" align="start" className="w-[380px] p-0" data-testid={`payment-sel-popover-${idx}`}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : historyData?.error ? (
          <div className="p-4 text-center text-xs text-muted-foreground">Failed to load bill history</div>
        ) : historyData ? (
          <div className="text-xs">
            {/* Header with vendor, property, bill copy */}
            <div className="px-3 py-2 border-b bg-muted/40">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${badgeColor}`}>
                    {sel}
                  </span>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200">
                    {seqLabel} Bill
                  </span>
                </div>
                <button
                  onClick={() => onViewPdf && onViewPdf(bill.bill_id)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium text-blue-600 hover:bg-blue-50 transition-colors border border-blue-200"
                  data-testid={`popover-bill-copy-${idx}`}
                >
                  <FileText className="h-3 w-3" />
                  Bill Copy
                </button>
              </div>
              {/* Vendor & Property info */}
              {(current?.vendor_name || current?.property_name || current?.service_address) && (
                <div className="space-y-0.5 mt-1">
                  {current?.vendor_name && (
                    <p className="text-[10px] font-semibold text-foreground truncate" title={current.vendor_name} data-testid={`popover-vendor-${idx}`}>
                      {current.vendor_name}
                    </p>
                  )}
                  {(current?.property_name || current?.service_address) && (
                    <p className="text-[10px] text-muted-foreground truncate" title={current?.service_address || current?.property_name} data-testid={`popover-property-${idx}`}>
                      {current?.property_name}{current?.service_address ? ` - ${current.service_address}` : ''}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Payment Status for Approved/Paid */}
            {isApprovedOrPaid && paymentInfo && (
              <div className="px-3 py-2 border-b bg-emerald-50/50">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                    paymentInfo.status === 'paid'
                      ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/20'
                      : 'bg-blue-500/15 text-blue-700 border-blue-500/20'
                  }`}>
                    {paymentInfo.status === 'paid' ? 'Paid' : 'Approved'}
                  </span>
                  {paymentInfo.paid_amount > 0 && (
                    <span className="font-mono font-semibold text-emerald-700">{fmtC(paymentInfo.paid_amount)}</span>
                  )}
                  {paymentInfo.transaction_reference && (
                    <span className="text-[10px] text-muted-foreground">Ref: {paymentInfo.transaction_reference}</span>
                  )}
                </div>
                {paymentInfo.transaction_date && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">Txn Date: {fmtD(paymentInfo.transaction_date)}</p>
                )}
                {paymentInfo.paid_by_name && (
                  <p className="text-[10px] text-muted-foreground mt-0.5">Paid By: {paymentInfo.paid_by_name}</p>
                )}
                {paymentInfo.refund === 'yes' && paymentInfo.refund_amount > 0 && (
                  <div className="mt-1 pt-1 border-t border-red-200">
                    <p className="text-[10px] font-semibold text-red-600">Refunded: {fmtC(paymentInfo.refund_amount)}</p>
                    {paymentInfo.refunded_by && <p className="text-[10px] text-muted-foreground">By: {paymentInfo.refunded_by}</p>}
                    {paymentInfo.refund_date && <p className="text-[10px] text-muted-foreground">Date: {fmtD(paymentInfo.refund_date)}</p>}
                    {(!paymentInfo.paid_amount || paymentInfo.paid_amount === 0) && (
                      <p className="text-[10px] text-blue-600 mt-0.5">Paid by 3rd Party — refunded from pulled amount</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Comparison Table */}
            <div className="px-3 py-2">
              {(historyData.month_sequence <= 1 && !previous) ? (
                <p className="text-muted-foreground text-center py-3">
                  This is the 1st bill for this account. No previous bill to compare.
                </p>
              ) : (
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="border-b border-border/60">
                      <th className="text-left py-1 font-semibold text-muted-foreground w-[100px]">Field</th>
                      <th className="text-right py-1 font-semibold text-muted-foreground">
                        Previous
                        {previous?.pf_batch_name && <span className="font-normal ml-1">({previous.pf_batch_name})</span>}
                      </th>
                      <th className="text-center py-1 w-5"></th>
                      <th className="text-right py-1 font-semibold text-blue-600">Current</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonRows.map(({ label, key, fmt }) => {
                      const prevVal = previous ? previous[key] : null;
                      const currVal = current ? current[key] : null;
                      const isDiff = prevVal != null && currVal != null && String(prevVal) !== String(currVal);
                      return (
                        <tr key={key} className="border-b border-border/30">
                          <td className="py-1 font-medium text-muted-foreground">{label}</td>
                          <td className="py-1 text-right font-mono">
                            {previous?.partial && prevVal == null ? (
                              <span className="text-muted-foreground/50">N/A</span>
                            ) : fmt(prevVal)}
                          </td>
                          <td className="py-1 text-center">
                            {isDiff && <ArrowRightLeft className="h-2.5 w-2.5 text-orange-400 mx-auto" />}
                          </td>
                          <td className={`py-1 text-right font-mono ${isDiff ? 'font-semibold text-blue-700' : ''}`}>
                            {fmt(currVal)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/** Duplicate badge with tooltip showing type and cross-PF links */
function DupBadge({ dups, accountId }) {
  const hasType1 = dups.some(d => d.type === 'exact_intra_pf' && !d.is_original);
  const type2 = dups.find(d => d.type === 'cross_pf_bill_id');
  const type3 = dups.find(d => d.type === 'cross_pf_field_match');

  let tooltipContent;
  if (type2) {
    const refs = type2.other_payment_files || [];
    tooltipContent = (
      <div className="space-y-1.5">
        <p className="font-semibold text-red-600">Duplicate Bill ID in other PF(s):</p>
        {refs.map((r, i) => (
          <a key={i} href={`/payments?pf=${r.payment_file_id}&account=${accountId}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-blue-600 hover:underline" data-testid={`dup-link-${i}`}>
            <ExternalLink className="h-3 w-3" />
            {r.batch_name} ({fmtD(r.generated_at)}) - {r.status}
          </a>
        ))}
      </div>
    );
  } else if (type3) {
    const refs = type3.other_payment_files || [];
    tooltipContent = (
      <div className="space-y-1.5">
        <p className="font-semibold text-red-600">Same invoice/account/address/EIN with different Bill ID:</p>
        <p className="text-muted-foreground text-[10px]">Matching: {type3.matching_fields?.invoice_number}, {type3.matching_fields?.account_number}</p>
        {refs.map((r, i) => (
          <a key={i} href={`/payments?pf=${r.payment_file_id}&account=${accountId}`} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 text-blue-600 hover:underline">
            <ExternalLink className="h-3 w-3" />
            {r.batch_name} ({fmtD(r.generated_at)}) - {r.status}
          </a>
        ))}
      </div>
    );
  } else if (hasType1) {
    tooltipContent = <p className="text-red-600">Duplicate row — remove or keep as needed</p>;
  } else {
    tooltipContent = <p className="text-emerald-700 font-medium">Original row</p>;
  }

  const isOriginal = dups.some(d => d.is_original);

  return (
    <Tooltip>
      <TooltipTrigger>
        <span className={`inline-flex items-center justify-center h-4 w-4 rounded text-[9px] font-bold ${
          isOriginal ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
          : hasType1 ? 'bg-red-500 text-white'
          : type2 ? 'bg-red-600 text-white border border-red-700 shadow-sm shadow-red-400/50'
          : type3 ? 'bg-orange-100 text-orange-700 border border-orange-300'
          : 'bg-gray-100 text-gray-500 border border-gray-300'
        }`} data-testid="dup-badge">
          {isOriginal ? 'O' : type2 ? 'X' : 'D'}
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-sm text-xs p-3">{tooltipContent}</TooltipContent>
    </Tooltip>
  );
}
